// src/pages/altitude-data/page.tsx
import React, { useEffect, useRef, useState } from 'react';
import * as Cesium from 'cesium';
import MVTImageryProvider from 'mvt-imagery-provider';
import BuildingInfo from '../components/mapview/BuildingInfo'
import BuildingInfoList from "../components/mapview/BuildingInfoList";
import PointPage from "../components/mapview/PointPopup";
import PoiLegend from "../components/mapview/PoiLegend";
import SceneDetailPanel from "../components/mapview/SceneDetailPanel";
import EnterpriseDetailModal from "../components/mapview/panels/EnterpriseDetailModal";
import { getPoiSceneDefByLabel } from '../../../config/poiSceneConfig';
import { fetchDynamicLayer, fetchBuildingsByObjectId, fetchRegionByGid, fetchScenesByTown, fetchByWKT } from "@/services/dataserver";

// 定义动态数据图层的配置
const DYNAMIC_LAYERS_CONFIG = {
  /* louyu_dian: {
    endpoint: 'getIntersectByBounds_nanjing_add_p',
    color: Cesium.Color.fromCssColorString('#ff6b6b'),
    label: '楼栋地址',
    geometryType: 'point' as const // 点要素
  }, */
  /* poi_dian: {
    endpoint: 'getIntersectByBounds_nanjing_poi_p',
    color: Cesium.Color.fromCssColorString('#34d399'),
    label: 'POI',
    geometryType: 'point' as const // 点要素
  }, */
  /* jianzhuwu_mian: {
    endpoint: 'getIntersectByBounds_nanjing_bui_s',
    color: Cesium.Color.fromCssColorString('#94a3b8'),
    label: '建筑物',
    geometryType: 'polygon' as const // 面要素
  }, */
  /* jumindi_mian: {
    endpoint: 'getIntersectByBounds_nanjing_bui_ss',
    color: Cesium.Color.fromCssColorString('#4ecdc4'),
    label: '居民地',
    geometryType: 'polygon' as const // 面要素
  }, */
  /* changjing_mian: {
    endpoint: 'getIntersectByBounds_nanjing_cangjing',
    color: Cesium.Color.fromCssColorString('#96ceb4'),
    label: '场景面',
    geometryType: 'polygon' as const // 面要素
  }, */

  //路网
  ditie_dian: {
    endpoint: 'getIntersectByBounds_nanjing_met_p',
    color: Cesium.Color.fromCssColorString('#60a5fa'),
    label: '地铁点',
    geometryType: 'point' as const
  },
  ditie_xian: {
    endpoint: 'getIntersectByBounds_nanjing_met_l',
    color: Cesium.Color.fromCssColorString('#60a5fa'),
    label: '地铁线',
    geometryType: 'polyline' as const
  },
  ditie_mian: {
    endpoint: 'getIntersectByBounds_nanjing_met_s',
    color: Cesium.Color.fromCssColorString('#60a5fa'),
    label: '地铁面',
    geometryType: 'polygon' as const
  },
  tielu_dian: {
    endpoint: 'getIntersectByBounds_nanjing_rai_p',
    color: Cesium.Color.fromCssColorString('#a78bfa'),
    label: '火车站点',
    geometryType: 'point' as const
  },
  tielu_xian: {
    endpoint: 'getIntersectByBounds_nanjing_rai_l',
    color: Cesium.Color.fromCssColorString('#a78bfa'),
    label: '火车站线',
    geometryType: 'polyline' as const
  },
  tielu_mian: {
    endpoint: 'getIntersectByBounds_nanjing_rai_s',
    color: Cesium.Color.fromCssColorString('#a78bfa'),
    label: '火车站面',
    geometryType: 'polygon' as const
  },
  /* gonglu_xian: {
    endpoint: 'getIntersectByBounds_nanjing_roa_cl',
    color: Cesium.Color.fromCssColorString('#45b7d1'),
    label: '道路中心线',
    geometryType: 'polyline' as const
  }, */
  /* gonglu_mian: {
    endpoint: 'getIntersectByBounds_nanjing_roa_s',
    color: Cesium.Color.fromCssColorString('#45b7d1'),
    label: '道路面',
    geometryType: 'polygon' as const
  }, */

  //行政区划
  quxian_dian: {
    endpoint: 'getIntersectByBounds_nanjing_qxd',
    color: Cesium.Color.fromCssColorString('#3b82f6'),
    label: '区县界',
    geometryType: 'point' as const
  },
  xiangzhen_mian: {
    endpoint: 'getIntersectByBounds_nanjing_str_s',
    color: Cesium.Color.fromCssColorString('#7c3aed'),
    label: '乡镇界',
    geometryType: 'polygon' as const
  },
  quxian_mian: {
    endpoint: 'getIntersectByBounds_nanjing_cou_s',
    color: Cesium.Color.fromCssColorString('#3b82f6'),
    label: '区县界',
    geometryType: 'polygon' as const
  },
  dishi_mian: {
    endpoint: 'getIntersectByBounds_nanjing_city_s',
    color: Cesium.Color.fromCssColorString('#ef4444'),
    label: '地市界',
    geometryType: 'polygon' as const
  },
};

type GeometryType = 'point' | 'polygon' | 'polyline';
type DynamicLayerKey = keyof typeof DYNAMIC_LAYERS_CONFIG;
export type MapType = 'street' | 'satellite';

/** 根据相机高度近似计算当前 Web Mercator 缩放级别 */
function getZoomLevel(viewer: any): number {
  const height = viewer.camera.positionCartographic.height;
  const zoom = Math.log2(40075016.686 / Math.max(height, 1));
  return Math.max(0, Math.min(19, Math.round(zoom)));
}

const MapPage: React.FC<{
  headerComponent?: React.ReactNode,
  activeLayers?: Record<string, boolean>,
  city?: string,
  cityCoordinates?: Record<string, [number, number]>,
  onLocationSelect?: (longitude: number, latitude: number, name: string, data?: Record<string, any>) => void,
  mapType?: MapType,
  onDataFromGridSearch?: any,
  drawingMode?: boolean;
  onDrawingChange?: (drawing: boolean) => void;
  layerControlCollapsed?: boolean;
}> = ({
  headerComponent,
  activeLayers = { building_white: false },
  city = "南京",
  cityCoordinates,
  onLocationSelect,
  mapType = 'street',
  onDataFromGridSearch,
  drawingMode = false,
  onDrawingChange,
  layerControlCollapsed = false,
}) => {
    const cesiumContainerRef = useRef<HTMLDivElement>(null);
    const viewerRef = useRef<any>(null);
    const buildingTilesetRef = useRef<any>(null);


    const regionEntityRef = useRef<Cesium.Entity[]>([]); // 网格搜索 行政区面
    const townAreaEntityRef = useRef<Cesium.Entity[]>([]); //网格搜索 场景面

    const dynamicEntitiesRef = useRef<Record<DynamicLayerKey, Cesium.Entity[]>>({
      //路网
      ditie_dian: [],
      ditie_xian: [],
      ditie_mian: [],
      tielu_dian: [],
      tielu_xian: [],
      tielu_mian: [],
      //行政区划
      quxian_dian: [],
      xiangzhen_mian: [],
      quxian_mian: [],
      dishi_mian: []
    });
    const fixedPointEntityRef = useRef<Cesium.Entity | null>(null);
    const enterpriseDataRef = useRef<Record<string, any> | null>(null);
    const [enterpriseDetailData, setEnterpriseDetailData] = useState<Record<string, any> | null>(null);
    const [showEnterpriseDetail, setShowEnterpriseDetail] = useState(false);
    const poiFixedPointBillboardRef = useRef<Cesium.Billboard | null>(null);
    const poiFixedCollectionRef = useRef<Cesium.BillboardCollection | null>(null);
    const poiEntitiesRef = useRef<Cesium.Entity[]>([]); // 用于POI列表点集合
    const selectedPoiLonLatRef = useRef<{ lon: number; lat: number } | null>(null); // 选中POI坐标，用于postRender跟踪
    const selectedMarkerRef = useRef<HTMLImageElement | null>(null); // HTML覆盖层图标DOM引用，直接更新位置
    const popupPosRef = useRef<{ x: number; y: number } | null>(null); // POI弹窗实时屏幕坐标（每帧更新）
    const activeLayersRef = useRef<Record<string, boolean>>(activeLayers);
    const [currentCity, setCurrentCity] = useState(city);
    const [viewerReady, setViewerReady] = useState(false);
    const [activeMapType, setActiveMapType] = useState<MapType>(mapType);
    const mapTypeRef = useRef<MapType>(mapType);
    mapTypeRef.current = activeMapType;
    const temporaryBuildingsRef = useRef<Cesium.Entity[]>([]); // 添加临时建筑物引用
    const [buildingInfoData, setBuildingInfoData] = useState<any>(null);
    const [buildingInfoPosition, setBuildingInfoPosition] = useState<{ x: number, y: number }>({ x: 0, y: 0 });
    const [showBuildingInfo, setShowBuildingInfo] = useState(false);
    // 场景面 POI列表
    const [poiListData, setPoiListData] = useState<any[]>([]);
    const poiListDataRef = useRef<any[]>([]); // 用于闭包中读取最新poiListData
    poiListDataRef.current = poiListData;
    const [showPoiList, setShowPoiList] = useState(false);
    // 场景面 POI详情
    const [selectedPoi, setSelectedPoi] = useState<any>(null);
    const [showPoiDetail, setShowPoiDetail] = useState(false);
    // 场景面 POI图例
    const [lxListData, setLxListData] = useState<any[]>([]); // 添加LxList状态
    const [showPoiLegend, setShowPoiLegend] = useState(false); // 添加图例显示状态
    // 网格搜索 场景面列表
    const [sceneList, setSceneList] = useState<any[]>([]);
    const [showSceneList, setShowSceneList] = useState(false);
    // 场景面详情弹窗
    const [sceneDetailData, setSceneDetailData] = useState<any>(null);
    const [showSceneDetail, setShowSceneDetail] = useState(false);
    const [scenePanelCollapsed, setScenePanelCollapsed] = useState(false);
    // 场景面类型筛选
    const [activeSceneTypes, setActiveSceneTypes] = useState<string[]>([]);
    // 切换场景面类型可见性（列表 + 地图面）
    const toggleSceneType = (type: string) => {
      setActiveSceneTypes(prev => {
        const next = prev.includes(type)
          ? prev.filter(t => t !== type)
          : [...prev, type];
        // 同步更新地图上场景面实体的可见性（仅影响场景面，不影响行政区划）
        [...regionEntityRef.current, ...townAreaEntityRef.current].forEach(entity => {
          try {
            const props = entity.properties?.getValue?.();
            if (props?.layerType !== '网格搜索场景面') return; // 跳过非场景面实体
            const extractType = props?.extractType || props?.originalData?.['提取'] || '';
            entity.show = next.includes(extractType || '未分类');
          } catch {
            // ignore
          }
        });
        return next;
      });
    };
    // 全部打开/全部关闭场景类型
    const toggleAllSceneTypes = () => {
      const allTypes = Array.from(new Set(sceneList.map(item => item.extractType || '未分类')));
      setActiveSceneTypes(prev => {
        const isAllActive = allTypes.every(t => prev.includes(t));
        const next = isAllActive ? [] : allTypes;
        [...regionEntityRef.current, ...townAreaEntityRef.current].forEach(entity => {
          try {
            const props = entity.properties?.getValue?.();
            if (props?.layerType !== '网格搜索场景面') return;
            const extractType = props?.extractType || props?.originalData?.['提取'] || '';
            entity.show = next.includes(extractType || '未分类');
          } catch {}
        });
        return next;
      });
    };
    // 场景面 选中高亮
    const [selectedSceneItem, setSelectedSceneItem] = useState<any>(null);
    const prevHighlightRef = useRef<any[]>([]); // [entity, origFill, origOutline, origWidth][]
    const sceneLabelRef = useRef<Cesium.Entity | null>(null);
    const lastClickedSceneGeomRef = useRef<string | null>(null); // 场景面列表中最后一次点击的场景面几何
    // 选中POI图标的屏幕位置（HTML覆盖层用）
    const [selectedMarkerIcon, setSelectedMarkerIcon] = useState<string | null>(null);

    // === 自定义网格绘制状态 ===
    const customGridPointsRef = useRef<[number, number][]>([]);
    const customGridPolygonsRef = useRef<{
      id: number;
      points: [number, number][];
      entity: Cesium.Entity;
      outlineEntity?: Cesium.Entity;
    }[]>([]);
    const customGridVisualEntitiesRef = useRef<Cesium.Entity[]>([]);
    const customGridIdCounterRef = useRef(0);
    const committedRef = useRef(false);
    const [drawPointCount, setDrawPointCount] = useState(0);
    const [drawPolygonCount, setDrawPolygonCount] = useState(0);

    const imageryLayersRef = useRef<{
      worldStreet?: any,
      worldStreet16?: any,
      nanjingSatellite?: any,
      nanjingStreet?: any,
    }>({});
    // 服务图层
    const pbfLayersRef = useRef<{
      louyu_dian?: any,
      poi_01?: any,
      jianzhuwu_mian?: any,
      jumindi_mian?: any,
      changjing_mian?: any,
      gonglu_xian?: any,
      gonglu_mian?: any,
      pickHandler?: Cesium.ScreenSpaceEventHandler,
      poiPickHandler?: Cesium.ScreenSpaceEventHandler,
      changjingClickHandler?: Cesium.ScreenSpaceEventHandler,
    }>({});
    // 添加DEM初始化标志
    const demInitializedRef = useRef(false);
    const lastRequestTimeRef = useRef<Map<DynamicLayerKey, number>>(new Map()); // 添加防抖时间跟踪
    const REQUEST_MIN_INTERVAL = 500; // 最小请求间隔，500毫秒

    // 更新activeLayersRef当activeLayers变化时
    useEffect(() => {
      activeLayersRef.current = activeLayers;
    }, [activeLayers]);

    // 更新地图类型
    useEffect(() => {
      setActiveMapType(mapType);
    }, [mapType]);

    // 获取当前视图范围的四至坐标
    const getViewBounds = (viewer: any): { lonMin: number; latMin: number; lonMax: number; latMax: number } | null => {
      try {
        const rectangle = viewer.camera.computeViewRectangle();
        if (!rectangle) return null;

        const west = Cesium.Math.toDegrees(rectangle.west);
        const south = Cesium.Math.toDegrees(rectangle.south);
        const east = Cesium.Math.toDegrees(rectangle.east);
        const north = Cesium.Math.toDegrees(rectangle.north);

        return {
          lonMin: west,
          latMin: south,
          lonMax: east,
          latMax: north
        };
      } catch (error) {
        console.error('Failed to get view bounds:', error);
        return null;
      }
    };

    // 请求后台接口获取数据
    const fetchDynamicData = async (endpoint: string, bounds: { lonMin: number; latMin: number; lonMax: number; latMax: number }) => {
      try {
        const data = await fetchDynamicLayer(endpoint, bounds);
        console.log(`Dynamic data received for ${endpoint}:`, data);
        return data;
      } catch (error) {
        console.error(`Failed to fetch dynamic data for ${endpoint}:`, error);
        return null;
      }
    };

    // 从WKT格式的geom字段解析坐标
    const parseWKTPoint = (wktString: string): [number, number] | null => {
      try {
        const match = wktString.match(/POINT\(\s*([\-+]?\d*\.?\d+)\s+([\-+]?\d*\.?\d+)\s*\)/);
        if (match) {
          const lon = parseFloat(match[1]);
          const lat = parseFloat(match[2]);
          if (!isNaN(lon) && !isNaN(lat)) {
            return [lon, lat];
          }
        }
        return null;
      } catch (error) {
        console.error('Failed to parse WKT:', error);
        return null;
      }
    };

    // 解析WKT线要素
    const parseWKTLineString = (wktString: string): number[][] | null => {
      try {
        // 匹配 LINESTRING(...) 格式
        const lineMatch = wktString.match(/LINESTRING\s*\(\s*([^)]+)\s*\)/);
        if (!lineMatch) {
          return null;
        }

        const coordsStr = lineMatch[1];
        const validCoords: number[][] = [];

        for (const coord of coordsStr.split(',')) {
          const parts = coord.trim().split(/\s+/);
          if (parts.length >= 2) {
            const lon = parseFloat(parts[0]);
            const lat = parseFloat(parts[1]);
            if (!isNaN(lon) && !isNaN(lat)) {
              validCoords.push([lon, lat]);
            }
          }
        }

        if (validCoords.length === 0) {
          return null;
        }
        return validCoords;
      } catch (error) {
        console.error('Failed to parse WKT LineString:', error);
        return null;
      }
    };

    // 解析WKT多边形
    const parseWKTPolygon = (wktString: string): number[][][] | null => {
      try {
        wktString = wktString.trim();

        let coordsArray: number[][][] = [];

        if (wktString.startsWith('POLYGON')) {
          // POLYGON((lon1 lat1, lon2 lat2, ...))
          // 只取第一个括号内的坐标（外环），忽略后续的内环
          const content = wktString.replace(/^POLYGON\s*\(\s*/, '').replace(/\s*\)\s*$/, '');
          // 找到第一个完整的坐标环
          let depth = 0;
          let start = -1;

          for (let i = 0; i < content.length; i++) {
            if (content[i] === '(') {
              if (depth === 0) start = i + 1;
              depth++;
            } else if (content[i] === ')') {
              depth--;
              if (depth === 0 && start !== -1) {
                const coordsStr = content.substring(start, i).trim();
                const validCoords = parseCoordinates(coordsStr);
                if (validCoords.length > 0) {
                  coordsArray.push(validCoords);
                }
                break; // 只取第一个环（外环）
              }
            }
          }
        }
        else if (wktString.startsWith('MULTIPOLYGON')) {
          // MULTIPOLYGON(((外环1),(内环1)),((外环2)))
          const content = wktString.replace(/^MULTIPOLYGON\s*\(\s*/, '').replace(/\s*\)\s*$/, '');

          // 分割各个子多边形
          let depth = 0;
          let start = 0;
          let subPolygons: string[] = [];

          for (let i = 0; i < content.length; i++) {
            if (content[i] === '(') {
              if (depth === 0) start = i;
              depth++;
            } else if (content[i] === ')') {
              depth--;
              if (depth === 0) {
                subPolygons.push(content.substring(start, i + 1));
              }
            }
          }

          // 对每个子多边形，只提取第一个环（外环）
          for (const subPolygon of subPolygons) {
            // 移除子多边形的外层括号
            const subContent = subPolygon.substring(1, subPolygon.length - 1).trim();
            let subDepth = 0;
            let subStart = -1;

            for (let i = 0; i < subContent.length; i++) {
              if (subContent[i] === '(') {
                if (subDepth === 0) subStart = i + 1;
                subDepth++;
              } else if (subContent[i] === ')') {
                subDepth--;
                if (subDepth === 0 && subStart !== -1) {
                  const coordsStr = subContent.substring(subStart, i).trim();
                  const validCoords = parseCoordinates(coordsStr);
                  if (validCoords.length > 0) {
                    coordsArray.push(validCoords);
                  }
                  break; // 只取第一个环（外环）
                }
              }
            }
          }
        }

        if (coordsArray.length === 0) {
          console.warn('No valid coordinates parsed from WKT:', wktString);
          return null;
        }
        return coordsArray;
      } catch (error) {
        console.error('Failed to parse WKT Polygon:', error, 'WKT:', wktString);
        return null;
      }
    };

    // 辅助函数：解析坐标字符串
    const parseCoordinates = (coordsStr: string): number[][] => {
      const validCoords: number[][] = [];

      for (const coord of coordsStr.split(',')) {
        const parts = coord.trim().split(/\s+/);
        if (parts.length >= 2) {
          const lon = parseFloat(parts[0]);
          const lat = parseFloat(parts[1]);
          if (!isNaN(lon) && !isNaN(lat)) {
            validCoords.push([lon, lat]);
          }
        }
      }

      // 确保闭合
      if (validCoords.length > 2 &&
        (Math.abs(validCoords[0][0] - validCoords[validCoords.length - 1][0]) > 1e-10 ||
          Math.abs(validCoords[0][1] - validCoords[validCoords.length - 1][1]) > 1e-10)) {
        validCoords.push([...validCoords[0]]);
      }

      return validCoords;
    };

    // 清除特定动态图层的所有实体
    const clearDynamicLayer = (layerKey: DynamicLayerKey) => {
      if (viewerRef.current && dynamicEntitiesRef.current[layerKey].length > 0) {
        const dataSource = viewerRef.current.entities;
        dynamicEntitiesRef.current[layerKey].forEach(entity => {
          dataSource.remove(entity);
        });
        dynamicEntitiesRef.current[layerKey] = [];
      }
    };

    // 清除所有动态图层
    const clearAllDynamicLayers = () => {
      Object.keys(DYNAMIC_LAYERS_CONFIG).forEach(key => {
        clearDynamicLayer(key as DynamicLayerKey);
      });
    };

    // 渲染点要素
    const renderPointFeature = (item: any, config: typeof DYNAMIC_LAYERS_CONFIG[keyof typeof DYNAMIC_LAYERS_CONFIG], index: number) => {
      let longitude: number | null = null;
      let latitude: number | null = null;

      if (item.geom && typeof item.geom === 'string') {
        const parsedCoords = parseWKTPoint(item.geom);
        if (parsedCoords) {
          [longitude, latitude] = parsedCoords;
        }
      }

      if (longitude === null && latitude === null && item.lon && item.lat) {
        longitude = parseFloat(item.lon.toString());
        latitude = parseFloat(item.lat.toString());
      }

      if (longitude !== null && latitude !== null && !isNaN(longitude) && !isNaN(latitude)) {
        console.log(`${config.label} point ${index}: ${longitude}, ${latitude}`);

        const entity = viewerRef.current.entities.add({
          position: Cesium.Cartesian3.fromDegrees(longitude, latitude),
          point: {
            pixelSize: 12,
            color: config.color,
            outlineColor: Cesium.Color.WHITE,
            outlineWidth: 2
          },
          properties: {
            originalData: item,
            layerType: config.label
          },
          show: true
        });

        return entity;
      }

      console.warn(`Invalid coordinates for ${config.label} point item:`, item);
      return null;
    };

    // 渲染线要素
    const renderPolylineFeature = (item: any, config: typeof DYNAMIC_LAYERS_CONFIG[keyof typeof DYNAMIC_LAYERS_CONFIG], index: number) => {
      let lineCoords: number[][] | null = null;

      if (item.geom && typeof item.geom === 'string') {
        lineCoords = parseWKTLineString(item.geom);
      }

      if (lineCoords && lineCoords.length > 1) {
        console.log(`${config.label} polyline ${index}:`, lineCoords);

        const entity = viewerRef.current.entities.add({
          polyline: {
            positions: lineCoords.map(([lon, lat]) =>
              Cesium.Cartesian3.fromDegrees(lon, lat)
            ),
            material: config.color,
            width: 3,
            clampToGround: true
          },
          properties: {
            originalData: item,
            layerType: config.label
          },
          show: true
        });

        return entity;
      }

      console.warn(`Invalid polyline coordinates for ${config.label} item:`, item);
      return null;
    };

    // 渲染面要素
    const renderPolygonFeature = (item: any, config: typeof DYNAMIC_LAYERS_CONFIG[keyof typeof DYNAMIC_LAYERS_CONFIG], index: number) => {
      // 根据 "提取" 字段解析颜色（网格搜索场景面按类型着色）
      let fillColor: Cesium.Color;
      let outlineColor: Cesium.Color;
      const extractType = item['提取'];
      if (extractType) {
        const def = getPoiSceneDefByLabel(extractType);
        fillColor = Cesium.Color.fromCssColorString(def.color);
        outlineColor = Cesium.Color.fromCssColorString(def.darkColor);
      } else {
        fillColor = config.color;
        outlineColor = config.color;
      }

      let polygonCoords: number[][][] | null = null;

      if (item.geom && typeof item.geom === 'string') {
        polygonCoords = parseWKTPolygon(item.geom);
      }

      if (polygonCoords && polygonCoords.length > 0) {
        console.log(`${config.label} polygon ${index}:`, polygonCoords, `提取=${extractType}`);

        // 创建所有多边形的实体并返回实体数组
        const entities: Cesium.Entity[] = [];

        polygonCoords.forEach((coords, polyIndex) => {
          const entity = viewerRef.current.entities.add({
            polygon: {
              hierarchy: new Cesium.PolygonHierarchy(
                coords.map(([lon, lat]) =>
                  Cesium.Cartesian3.fromDegrees(lon, lat)
                )
              ),
              material: fillColor.withAlpha(0.3), // 半透明填充
              outline: true,
              outlineColor: outlineColor,
              outlineWidth: 2,
              height: 0
            },
            properties: {
              originalData: item,
              layerType: config.label,
              polygonIndex: polyIndex,
              extractType: extractType
            },
            show: true
          });
          entities.push(entity);
        });

        return entities;
      }

      console.warn(`Invalid polygon coordinates for ${config.label} item:`, item);
      return null;
    };

    // 渲染动态数据到地图
    const renderDynamicData = (layerKey: DynamicLayerKey, data: any) => {
      if (!viewerRef.current) return;

      // 关键修复：在渲染数据前检查图层当前是否仍然处于开启状态
      const shouldShowLayer = activeLayersRef.current?.[layerKey] || false;
      if (!shouldShowLayer) {
        console.log(`Skipping render for ${DYNAMIC_LAYERS_CONFIG[layerKey].label} - layer is now disabled`);
        return;
      }

      // 清除该图层的现有实体
      clearDynamicLayer(layerKey);

      const config = DYNAMIC_LAYERS_CONFIG[layerKey];
      let dataArray: any[] = [];

      if (Array.isArray(data)) {
        dataArray = data;
      } else if (data && typeof data === 'object') {
        const arrayKeys = Object.keys(data).filter(key => Array.isArray(data[key]));
        if (arrayKeys.length > 0) {
          dataArray = data[arrayKeys[0]];
        } else {
          dataArray = [data];
        }
      } else {
        dataArray = Array.isArray(data) ? data : [];
      }

      console.log(`Rendering ${dataArray.length} ${config.label} features`);

      dataArray.forEach((item: any, index: number) => {
        if (!item || typeof item !== 'object') {
          console.warn(`Invalid item format at index ${index} for ${config.label}:`, item);
          return;
        }

        let entities: Cesium.Entity[] | Cesium.Entity | null = null;

        switch (config.geometryType) {
          case 'point':
            entities = renderPointFeature(item, config, index);
            break;
          case 'polygon':
            entities = renderPolygonFeature(item, config, index);
            break;
          case 'polyline':
            entities = renderPolylineFeature(item, config, index);
            break;
          default:
            console.warn(`Unsupported geometry type`);
            break;
        }

        if (entities) {
          // 处理单个实体或实体数组
          if (Array.isArray(entities)) {
            dynamicEntitiesRef.current[layerKey].push(...entities);
          } else {
            dynamicEntitiesRef.current[layerKey].push(entities);
          }
        }
      });

      if (dataArray.length === 0) {
        console.log(`No valid ${config.label} features to render`);
      }
    };

    // 处理视图变化并请求数据（针对特定图层）
    const handleViewChangeForLayer = async (layerKey: DynamicLayerKey) => {
      if (!viewerRef.current) return;

      const currentTime = Date.now();
      const lastRequestTime = lastRequestTimeRef.current.get(layerKey) || 0;

      // 检查是否在最小间隔内重复请求
      if (currentTime - lastRequestTime < REQUEST_MIN_INTERVAL) {
        console.log(`Skipping ${DYNAMIC_LAYERS_CONFIG[layerKey].label} request - too soon after last request`);
        return;
      }

      const bounds = getViewBounds(viewerRef.current);
      if (!bounds) {
        console.log('No bounds available');
        return;
      }

      // 定义五级行政区划白名单
      const ADMIN_DIVISION_LAYERS: DynamicLayerKey[] = [
        'ditie_dian',
        'ditie_xian',
        'ditie_mian',
        'tielu_dian',
        'tielu_xian',
        'tielu_mian',
        'quxian_dian',
        'xiangzhen_mian',
        'quxian_mian',
        'dishi_mian'
      ];

      const isAdministrativeDivision = ADMIN_DIVISION_LAYERS.includes(layerKey);

      // 获取相机高度并添加限制：超过2000时不加载图层（但行政区划除外）
      const cameraPosition = viewerRef.current.camera.position;
      const cartographic = Cesium.Cartographic.fromCartesian(cameraPosition);
      const cameraHeight = cartographic.height;

      // 只对非行政区划图层应用高度限制
      if (!isAdministrativeDivision && cameraHeight > 2000) {
        console.log(`Skipping ${DYNAMIC_LAYERS_CONFIG[layerKey].label} request - camera height ${cameraHeight} > 2000`);
        clearAllDynamicLayers();
        return;
      }

      const config = DYNAMIC_LAYERS_CONFIG[layerKey];
      console.log(`Requesting ${config.label} data for bounds:`, bounds);

      // 更新最后请求时间
      lastRequestTimeRef.current.set(layerKey, currentTime);

      const data = await fetchDynamicData(config.endpoint, bounds);
      if (data !== null) {
        renderDynamicData(layerKey, data);
      } else {
        console.log(`No ${config.label} data received from API`);
      }
    };

    // 处理视图变化 - 检查所有动态图层是否需要加载数据
    const handleViewChange = () => {
      Object.keys(DYNAMIC_LAYERS_CONFIG).forEach(key => {
        const layerKey = key as DynamicLayerKey;
        const shouldShowLayer = activeLayersRef.current?.[layerKey] || false;
        if (shouldShowLayer) {
          handleViewChangeForLayer(layerKey);
        }
      });
    };

    // 根据当前缩放级别更新街道图层的可见性（0-15 用 quanguo，16-19 用 quanguo_16_19）
    const updateZoomLevelLayers = () => {
      const layers = imageryLayersRef.current;
      if (!layers || !viewerRef.current) return;
      if (mapTypeRef.current !== 'street') return;

      const zoom = getZoomLevel(viewerRef.current);
      if (zoom <= 15) {
        // 0-15 级：显示 quanguo，隐藏 quanguo_16_19
        if (layers.worldStreet) layers.worldStreet.show = true;
        if (layers.worldStreet16) layers.worldStreet16.show = false;
      } else {
        // 16-19 级：显示 quanguo_16_19，隐藏 quanguo
        if (layers.worldStreet) layers.worldStreet.show = false;
        if (layers.worldStreet16) layers.worldStreet16.show = true;
      }
    };

    // 更新地图类型图层可见性
    const updateMapTypeLayers = (mapType: MapType, currentCity: string) => {
      if (!viewerRef.current || !imageryLayersRef.current) return;

      const layers = imageryLayersRef.current;

      if (mapType === 'satellite') {
        // 卫星图模式：只显示卫星图
        if (layers.worldStreet) layers.worldStreet.show = false;
        if (layers.worldStreet16) layers.worldStreet16.show = false;
        if (layers.nanjingStreet) layers.nanjingStreet.show = false;
        if (layers.nanjingSatellite) layers.nanjingSatellite.show = true;
      } else {
        // 街道图模式：nanjing_vector 始终显示，quanguo/quanguo_16_19 按 zoom 切换
        if (layers.nanjingSatellite) layers.nanjingSatellite.show = false;
        if (layers.nanjingStreet) layers.nanjingStreet.show = true;
        updateZoomLevelLayers();
      }
    };

    // 初始化Cesium
    useEffect(() => {
      let viewer: any = null;

      const initCesium = async () => {
        try {
          if (cesiumContainerRef.current) {
            viewer = new Cesium.Viewer(cesiumContainerRef.current, {
              scene3DOnly: true,
              animation: false,
              baseLayerPicker: false,
              geocoder: false,
              timeline: false,
              sceneModePicker: false,
              selectionIndicator: false,
              navigationHelpButton: false,
              infoBox: false,
              fullscreenButton: false,
              homeButton: false,
              shouldAnimate: false,
            });

            viewerRef.current = viewer;
            (window as any).__cesiumViewer = viewer;

            const customWMTSLayer: any = new Cesium.WebMapTileServiceImageryProvider({
              url: "https://www.daditu.com.cn/quanqiu/service/wmts",
              layer: "quanguo",
              style: "",
              format: "image/png",
              tileMatrixSetID: "EPSG:3857_quanguo",
              tileMatrixLabels: Array.from({ length: 19 }, (_, i) => `EPSG:3857_quanguo:${i}`),
              maximumLevel: 18,
              tileWidth: 256,
              tileHeight: 256,
            });

            const customWMTSLayer16: any = new Cesium.WebMapTileServiceImageryProvider({
              url: "https://www.daditu.com.cn/quanqiu16/service/wmts",
              layer: "quanguo_16_19",
              style: "",
              format: "image/png",
              tileMatrixSetID: "EPSG:3857_quanguo_16_19",
              tileMatrixLabels: Array.from({ length: 19 }, (_, i) => `EPSG:3857_quanguo_16_19:${i}`),
              maximumLevel: 18,
              tileWidth: 256,
              tileHeight: 256,
            });

            const nanjingWMTSLayer: any = new Cesium.WebMapTileServiceImageryProvider({
              url: "https://www.daditu.com.cn/quanqiu/service/wmts",
              layer: "nanjing",
              style: "",
              format: "image/png",
              tileMatrixSetID: "EPSG:3857_nanjing",
              tileMatrixLabels: Array.from({ length: 19 }, (_, i) => `EPSG:3857_nanjing:${i}`),
              maximumLevel: 18,
            });

            const nanjingVectorWMTSLayer: any = new Cesium.WebMapTileServiceImageryProvider({
              url: "https://www.daditu.com.cn/quanqiu/service/wmts",
              layer: "nanjing_vector",
              style: "",
              format: "image/png",
              tileMatrixSetID: "EPSG:3857_nanjing_vector",
              tileMatrixLabels: Array.from({ length: 19 }, (_, i) => `EPSG:3857_nanjing_vector:${i}`),
              maximumLevel: 18,
            });

            // 添加图层到viewer并保存引用 - 所有图层都添加，包括您选中的两个世界街道图层
            const worldStreetLayer = viewer.imageryLayers.addImageryProvider(customWMTSLayer);
            const worldStreetLayer16 = viewer.imageryLayers.addImageryProvider(customWMTSLayer16);
            const nanjingSatelliteLayer = viewer.imageryLayers.addImageryProvider(nanjingWMTSLayer);
            const nanjingStreetLayer = viewer.imageryLayers.addImageryProvider(nanjingVectorWMTSLayer);

            imageryLayersRef.current = {
              worldStreet: worldStreetLayer,
              worldStreet16: worldStreetLayer16,
              nanjingSatellite: nanjingSatelliteLayer,
              nanjingStreet: nanjingStreetLayer
            };

            // 立即设置初始可见性：街道图模式默认显示 quanguo + nanjing_vector，隐藏 quanguo_16_19 和卫星图
            worldStreetLayer.show = true;
            worldStreetLayer16.show = false;
            nanjingSatelliteLayer.show = false;
            nanjingStreetLayer.show = true;

            // 控制高度
            const controller = viewer.scene.screenSpaceCameraController;
            controller.maximumZoomDistance = 20000000;
            controller.minimumZoomDistance = 200;

            //关闭默认版权
            viewer._cesiumWidget._creditContainer.style.display = "none";
            viewer.scene.backgroundColor = Cesium.Color.WHITE;
            viewer.scene.globe.baseColor = Cesium.Color.WHITE;
            viewer.scene.fxaa = false;
            viewer.scene._hdr = false;

            viewer.camera.changed.addEventListener(function () {
              if (viewer.camera._suspendTerrainAdjustment && viewer.scene.mode === Cesium.SceneMode.SCENE3D) {
                viewer.camera._suspendTerrainAdjustment = false;
                viewer.camera._adjustHeightForTerrain();
              }
            });

            // 每帧更新HTML覆盖层图标和弹窗位置——地图拖拽时连续跟随
            viewer.scene.postRender.addEventListener(() => {
              const lonLat = selectedPoiLonLatRef.current;
              if (lonLat && viewer.scene) {
                const cp = Cesium.Cartesian3.fromDegrees(lonLat.lon, lonLat.lat);
                const canvasPos = viewer.scene.cartesianToCanvasCoordinates(cp);
                if (canvasPos && (canvasPos.x > 0 || canvasPos.y > 0)) {
                  // 图标
                  const imgEl = selectedMarkerRef.current;
                  if (imgEl) {
                    imgEl.style.left = canvasPos.x + 'px';
                    imgEl.style.top = canvasPos.y + 'px';
                  }
                  // 弹窗（PointPopup 通过 RAF 读取此 ref）
                  popupPosRef.current = { x: canvasPos.x, y: canvasPos.y };
                }
              }
            });

            let moveEndTimeout: number | null = null;
            let zoomLayerTimeout: number | null = null;
            viewer.camera.moveEnd.addEventListener(() => {
              if (moveEndTimeout !== null) {
                clearTimeout(moveEndTimeout);
              }
              moveEndTimeout = window.setTimeout(() => {
                handleViewChange();
              }, 500);

              // 缩放级别变化时快速切换图层（独立防抖，响应更快）
              if (zoomLayerTimeout !== null) {
                clearTimeout(zoomLayerTimeout);
              }
              zoomLayerTimeout = window.setTimeout(() => {
                updateZoomLevelLayers();
              }, 150);
            });

            setTimeout(() => {
              // 保存默认地形提供者
              setViewerReady(true);
            }, 2000);

            console.log('Cesium viewer initialized successfully');
          }
        } catch (error) {
          console.error('Failed to initialize Cesium:', error);
        }
      };

      initCesium();

      return () => {
        if (viewer) {
          viewer.destroy();
        }
        if (fixedPointEntityRef.current && viewerRef.current) {
          viewerRef.current.entities.remove(fixedPointEntityRef.current);
          fixedPointEntityRef.current = null;
        }
        clearAllDynamicLayers();
      };
    }, []);


    // === 清除 楼栋地址 图层函数 ===
    const clearLouyuLayer = () => {
      if (!viewerRef.current) return;

      // 清除 louyu_dian
      if (pbfLayersRef.current.louyu_dian) {
        viewerRef.current.imageryLayers.remove(pbfLayersRef.current.louyu_dian);
        pbfLayersRef.current.louyu_dian = undefined;
      }

      // 清除拾取事件处理器
      if (pbfLayersRef.current.pickHandler) {
        pbfLayersRef.current.pickHandler.destroy();
        pbfLayersRef.current.pickHandler = undefined;
      }

      // 隐藏楼栋信息弹窗
      const louyuOverlay = document.getElementById('louyuInfoOverlay');
      if (louyuOverlay) {
        louyuOverlay.style.display = 'none';
      }
    };
    // === 加载 楼栋地址 图层函数 ===
    const loadLouyuLayer = () => {
      if (!viewerRef.current || !viewerReady) {
        return;
      }

      const louyuStyle: any = {
        version: 8,
        glyphs: 'https://www.daditu.com.cn/map_resource/fonts/{fontstack}/{range}.pbf',
        sources: {
          'nanjing-louyu': {
            type: 'vector',
            tiles: ['https://www.daditu.com.cn/map_resource/nanjing/mvt/{z}/{x}/{y}.mvt'],
          }
        },
        layers: [
          {
            id: 'ADD_P_1@0_10JI(0_24)',
            type: 'circle',
            source: 'nanjing-louyu',
            'source-layer': 'ADD_P_1@0_10JI',
            paint: {
              'circle-radius': 5,
              'circle-color': '#ff6b6b',
              'circle-stroke-width': 1,
              'circle-stroke-color': '#ffffff'
            }
          },
          {
            id: 'ADD_P_1@11_12JI(0_24)',
            type: 'circle',
            source: 'nanjing-louyu',
            'source-layer': 'ADD_P_1@11_12JI',
            paint: {
              'circle-radius': 5,
              'circle-color': '#ff6b6b',
              'circle-stroke-width': 1,
              'circle-stroke-color': '#ffffff'
            }
          },
          {
            id: 'ADD_P_1@13_14JI(0_24)',
            type: 'circle',
            source: 'nanjing-louyu',
            'source-layer': 'ADD_P_1@13_14JI',
            paint: {
              'circle-radius': 5,
              'circle-color': '#ff6b6b',
              'circle-stroke-width': 1,
              'circle-stroke-color': '#ffffff'
            }
          },
          {
            id: 'ADD_P_1@15JI',
            type: 'circle',
            source: 'nanjing-louyu',
            'source-layer': 'ADD_P_1@15JI',
            paint: {
              'circle-radius': 5,
              'circle-color': '#ff6b6b',
              'circle-stroke-width': 1,
              'circle-stroke-color': '#ffffff'
            }
          },
          {
            id: 'ADD_P_1@16JI',
            type: 'circle',
            source: 'nanjing-louyu',
            'source-layer': 'ADD_P_1@16JI',
            paint: {
              'circle-radius': 5,
              'circle-color': '#ff6b6b',
              'circle-stroke-width': 1,
              'circle-stroke-color': '#ffffff'
            }
          },
          {
            id: 'ADD_P_1@17JI',
            type: 'circle',
            source: 'nanjing-louyu',
            'source-layer': 'ADD_P_1@17JI',
            paint: {
              'circle-radius': 5,
              'circle-color': '#ff6b6b',
              'circle-stroke-width': 1,
              'circle-stroke-color': '#ffffff'
            }
          },
          {
            id: 'ADD_P_1@18JI',
            type: 'circle',
            source: 'nanjing-louyu',
            'source-layer': 'ADD_P_1@18JI',
            paint: {
              'circle-radius': 5,
              'circle-color': '#ff6b6b',
              'circle-stroke-width': 1,
              'circle-stroke-color': '#ffffff'
            }
          },
          /* {
            id: 'ADD_P_1@18JI-label',
            type: 'symbol',
            source: 'nanjing-louyu',
            'source-layer': 'ADD_P_1@18JI',
            layout: {
              'text-field': "{BUD}", // 固定字符串
              'text-font': ['NotoCJK'],
              'text-size': 12,
              'text-anchor': 'bottom',
              'text-offset': [0, -1]
            },
            paint: {
              'text-color': '#1a1a1a',        // 柔和黑，避免纯黑锯齿
              'text-halo-color': '#ffffff',
              'text-halo-width': 1.8,         // 加宽光晕
              'text-halo-blur': 0.5           // 轻微模糊，让光晕更自然
            }
          } */
        ]
      };

      MVTImageryProvider.fromUrl(louyuStyle, {
        enablePickFeatures: true,
      })
        .then((louyuProvider: any) => {
          const louyuLayer = viewerRef.current.imageryLayers.addImageryProvider(louyuProvider);
          louyuLayer.show = true;
          pbfLayersRef.current.louyu_dian = louyuLayer;

          // 创建弹窗元素
          const louyuOverlay = createLouyuInfoOverlay();
          let currentBudValue: string | null = null;
          let isPicking = false;

          // 添加节流函数
          const throttlePick = (callback: () => void, delay: number) => {
            if (isPicking) return;
            isPicking = true;
            setTimeout(() => {
              callback();
              isPicking = false;
            }, delay);
          };

          // 获取当前缩放层级的辅助函数
          const getCurrentZoomLevel = () => {
            const viewer = viewerRef.current;
            if (!viewer) return 0;

            const camera = viewer.camera;
            const canvas = viewer.canvas;

            // 使用 Cesium 的标准方法计算缩放层级
            const frustum = camera.frustum;
            const fovy = frustum.fov;
            const aspectRatio = canvas.clientWidth / canvas.clientHeight;

            // 计算垂直方向的米每像素
            const cartographic = Cesium.Cartographic.fromCartesian(camera.position);
            const height = cartographic.height;
            const metersPerPixelVertical = (2 * height * Math.tan(fovy / 2)) / canvas.clientHeight;

            // 计算缩放层级 (基于 Web Mercator 的标准)
            const earthCircumference = 2 * Math.PI * 6378137; // 赤道周长
            const zoomLevel = Math.log2(earthCircumference / (metersPerPixelVertical * 256));

            return Math.round(zoomLevel);
          };

          // ---------- 注册鼠标滑过事件（MOUSE_MOVE）----------
          const handler = new Cesium.ScreenSpaceEventHandler(viewerRef.current.scene.canvas);
          handler.setInputAction((movement: { endPosition: Cesium.Cartesian2 }) => {
            const viewer = viewerRef.current;

            // 获取当前缩放层级
            const currentZoom = getCurrentZoomLevel();

            // 只在 17 级及以上的层级显示弹窗（17级、18级、19级等）
            if (currentZoom < 17) {
              if (currentBudValue !== null) {
                currentBudValue = null;
                louyuOverlay.style.display = 'none';
              }
              return;
            }

            // 节流拾取操作，避免频繁调用
            throttlePick(() => {
              const pickRay = viewer.camera.getPickRay(movement.endPosition);
              const featuresPromise = viewer.imageryLayers.pickImageryLayerFeatures(pickRay, viewer.scene);

              if (!Cesium.defined(featuresPromise)) {
                if (currentBudValue !== null) {
                  currentBudValue = null;
                  louyuOverlay.style.display = 'none';
                }
                return;
              }

              Promise.resolve(featuresPromise)
                .then((features: any[]) => {
                  if (features && features.length > 0) {
                    let budValue = '未知';
                    const feature = features[0];

                    if (feature.data) {
                      const layerKeys = Object.keys(feature.data);
                      for (const layerKey of layerKeys) {
                        const layerData = feature.data[layerKey];
                        if (Array.isArray(layerData) && layerData.length > 0 && layerData[0].BUD) {
                          budValue = layerData[0].BUD;
                          break;
                        }
                      }
                    }

                    if (budValue !== '未知') {
                      if (budValue !== currentBudValue) {
                        currentBudValue = budValue;
                        louyuOverlay.style.display = 'block';
                        louyuOverlay.textContent = budValue;
                      }
                      // 更新位置
                      louyuOverlay.style.bottom = (viewer.canvas.clientHeight - movement.endPosition.y + 10) + 'px';
                      louyuOverlay.style.left = (movement.endPosition.x - louyuOverlay.offsetWidth / 2) + 'px';
                    } else {
                      if (currentBudValue !== null) {
                        currentBudValue = null;
                        louyuOverlay.style.display = 'none';
                      }
                    }
                  } else {
                    if (currentBudValue !== null) {
                      currentBudValue = null;
                      louyuOverlay.style.display = 'none';
                    }
                  }
                })
                .catch((err) => {
                  console.error('拾取过程出错:', err);
                  if (currentBudValue !== null) {
                    currentBudValue = null;
                    louyuOverlay.style.display = 'none';
                  }
                });
            }, 200); // 50ms 节流

          }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

          // 保存 handler 以便后续销毁
          pbfLayersRef.current.pickHandler = handler;

        })
        .catch((error: any) => {
          console.error('楼栋地址 MVT图层加载失败:', error);
        });
    };
    // 创建楼栋信息弹窗元素
    const createLouyuInfoOverlay = () => {
      let overlay = document.getElementById('louyuInfoOverlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'louyuInfoOverlay';
        overlay.style.position = 'absolute';
        overlay.style.display = 'none';
        overlay.style.padding = '6px 10px';
        overlay.style.backgroundColor = 'white';
        overlay.style.borderRadius = '4px';
        overlay.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
        overlay.style.fontFamily = 'sans-serif';
        overlay.style.fontSize = '12px';
        overlay.style.color = '#000';
        overlay.style.pointerEvents = 'none';
        overlay.style.zIndex = '9999';
        overlay.style.minWidth = '80px';
        overlay.style.textAlign = 'center';
        document.body.appendChild(overlay);
      }
      return overlay;
    };

    // === 清除 POI 图层函数 ===
    const clearPbfLayers = () => {
      if (!viewerRef.current) return;

      // 清除 nanjing_poi_01
      if (pbfLayersRef.current.poi_01) {
        viewerRef.current.imageryLayers.remove(pbfLayersRef.current.poi_01);
        pbfLayersRef.current.poi_01 = undefined;
      }
      // 清除场景面点击处理器
      if (pbfLayersRef.current.poiPickHandler) {
        pbfLayersRef.current.poiPickHandler.destroy();
        pbfLayersRef.current.poiPickHandler = undefined;
      }
    };
    // === 加载 POI 图层函数 ===
    const loadPbfLayers = () => {
      if (!viewerRef.current || !viewerReady) {
        return;
      }

      const pbfStyle01: any = {
        version: 8,
        glyphs: 'https://www.daditu.com.cn/map_resource/fonts/{fontstack}/{range}.pbf',
        sources: {
          'nanjing-poi': {
            type: 'vector',
            tiles: ['https://www.daditu.com.cn/map_resource/nanjing/mvt3/{z}/{x}/{y}.mvt'],
          }
        },
        layers: [
          {
            id: 'POI_1@0_1020260415(0_24)',
            type: 'circle',
            source: 'nanjing-poi',
            'source-layer': 'POI_1@0_1020260415',
            paint: {
              'circle-radius': 5,
              'circle-color': '#34d399',
              'circle-stroke-width': 1,
              'circle-stroke-color': '#ffffff'
            }
          },
          {
            id: 'POI_P_1@11_1220260415(0_24)',
            type: 'circle',
            source: 'nanjing-poi',
            'source-layer': 'POI_P_1@11_1220260415',
            paint: {
              'circle-radius': 5,
              'circle-color': '#34d399',
              'circle-stroke-width': 1,
              'circle-stroke-color': '#ffffff'
            }
          },
          {
            id: 'POI_P_1@13_1420260415(0_24)',
            type: 'circle',
            source: 'nanjing-poi',
            'source-layer': 'POI_P_1@13_1420260415',
            paint: {
              'circle-radius': 5,
              'circle-color': '#34d399',
              'circle-stroke-width': 1,
              'circle-stroke-color': '#ffffff'
            }
          },
          {
            id: 'POI_P_1@1520260415(0_24)',
            type: 'circle',
            source: 'nanjing-poi',
            'source-layer': 'POI_P_1@1520260415',
            paint: {
              'circle-radius': 5,
              'circle-color': '#34d399',
              'circle-stroke-width': 1,
              'circle-stroke-color': '#ffffff'
            }
          },
          {
            id: 'POI_P_1@1620260415(0_24)',
            type: 'circle',
            source: 'nanjing-poi',
            'source-layer': 'POI_P_1@1620260415',
            paint: {
              'circle-radius': 5,
              'circle-color': '#34d399',
              'circle-stroke-width': 1,
              'circle-stroke-color': '#ffffff'
            }
          },
          {
            id: 'POI_P_1@17_1820260413(0_24)',
            type: 'circle',
            source: 'nanjing-poi',
            'source-layer': 'POI_P_1@17_1820260413',
            paint: {
              'circle-radius': 5,
              'circle-color': '#34d399',
              'circle-stroke-width': 1,
              'circle-stroke-color': '#ffffff'
            }
          },
          /* {
            id: 'POI_P_1@17_1820260413(0_24)-label',
            type: 'symbol',
            source: 'nanjing-poi',
            'source-layer': 'POI_P_1@17_1820260413',
            filter: ['all', ['>=', ['zoom'], 18], ['<=', ['zoom'], 18]], // 只在18级显示
            layout: {
              'text-field': "{NAME}", // 显示 NAME 字段
              'text-font': ['NotoCJK'],
              'text-size': 12,
              'text-anchor': 'bottom',
              'text-offset': [0, -1]
            },
            paint: {
              'text-color': '#1a1a1a',        // 柔和黑，避免纯黑锯齿
              'text-halo-color': '#ffffff',
              'text-halo-width': 1.8,         // 加宽光晕
              'text-halo-blur': 0.5           // 轻微模糊，让光晕更自然
            }
          } */

        ]
      };

      MVTImageryProvider.fromUrl(pbfStyle01, {
        enablePickFeatures: true,
      })
        .then((pbfProvider: any) => {
          const pbfLayer01 = viewerRef.current.imageryLayers.addImageryProvider(pbfProvider);
          pbfLayer01.show = true; // 确保图层显示
          pbfLayersRef.current.poi_01 = pbfLayer01;

          // 创建 POI 弹窗元素
          const poiOverlay = createPoiInfoOverlay();
          let currentPoiName: string | null = null;
          let isPicking = false;

          // 添加节流函数
          const throttlePick = (callback: () => void, delay: number) => {
            if (isPicking) return;
            isPicking = true;
            setTimeout(() => {
              callback();
              isPicking = false;
            }, delay);
          };

          // 获取当前缩放层级的辅助函数
          const getCurrentZoomLevel = () => {
            const viewer = viewerRef.current;
            if (!viewer) return 0;

            const camera = viewer.camera;
            const canvas = viewer.canvas;

            // 使用 Cesium 的标准方法计算缩放层级
            const frustum = camera.frustum;
            const fovy = frustum.fov;
            const aspectRatio = canvas.clientWidth / canvas.clientHeight;

            // 计算垂直方向的米每像素
            const cartographic = Cesium.Cartographic.fromCartesian(camera.position);
            const height = cartographic.height;
            const metersPerPixelVertical = (2 * height * Math.tan(fovy / 2)) / canvas.clientHeight;

            // 计算缩放层级 (基于 Web Mercator 的标准)
            const earthCircumference = 2 * Math.PI * 6378137; // 赤道周长
            const zoomLevel = Math.log2(earthCircumference / (metersPerPixelVertical * 256));

            return Math.round(zoomLevel);
          };

          // ---------- 注册鼠标滑过事件（MOUSE_MOVE）----------
          const handler = new Cesium.ScreenSpaceEventHandler(viewerRef.current.scene.canvas);
          handler.setInputAction((movement: { endPosition: Cesium.Cartesian2 }) => {
            const viewer = viewerRef.current;

            // 获取当前缩放层级
            const currentZoom = getCurrentZoomLevel();

            // 只在 17 级及以上的层级显示弹窗
            if (currentZoom < 17) {
              if (currentPoiName !== null) {
                currentPoiName = null;
                poiOverlay.style.display = 'none';
              }
              return;
            }

            // 节流拾取操作，避免频繁调用
            throttlePick(() => {
              const pickRay = viewer.camera.getPickRay(movement.endPosition);
              const featuresPromise = viewer.imageryLayers.pickImageryLayerFeatures(pickRay, viewer.scene);

              if (!Cesium.defined(featuresPromise)) {
                if (currentPoiName !== null) {
                  currentPoiName = null;
                  poiOverlay.style.display = 'none';
                }
                return;
              }

              Promise.resolve(featuresPromise)
                .then((features: any[]) => {
                  if (features && features.length > 0) {
                    let poiName = '未知';
                    const feature = features[0];

                    if (feature.data) {
                      const layerKeys = Object.keys(feature.data);
                      for (const layerKey of layerKeys) {
                        const layerData = feature.data[layerKey];
                        if (Array.isArray(layerData) && layerData.length > 0 && layerData[0].NAME) {
                          poiName = layerData[0].NAME;
                          break;
                        }
                      }
                    }

                    if (poiName !== '未知') {
                      if (poiName !== currentPoiName) {
                        currentPoiName = poiName;
                        poiOverlay.style.display = 'block';
                        poiOverlay.textContent = poiName;
                      }
                      // 更新位置
                      poiOverlay.style.bottom = (viewer.canvas.clientHeight - movement.endPosition.y + 10) + 'px';
                      poiOverlay.style.left = (movement.endPosition.x - poiOverlay.offsetWidth / 2) + 'px';
                    } else {
                      if (currentPoiName !== null) {
                        currentPoiName = null;
                        poiOverlay.style.display = 'none';
                      }
                    }
                  } else {
                    if (currentPoiName !== null) {
                      currentPoiName = null;
                      poiOverlay.style.display = 'none';
                    }
                  }
                })
                .catch((err) => {
                  console.error('POI 拾取过程出错:', err);
                  if (currentPoiName !== null) {
                    currentPoiName = null;
                    poiOverlay.style.display = 'none';
                  }
                });
            }, 200); // 50ms 节流

          }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

          // 保存 handler 以便后续销毁
          pbfLayersRef.current.poiPickHandler = handler;

        })
        .catch((error: any) => {
          console.error('nanjing_poi PBF图层加载失败:', error);
        });
    };
    // 创建 POI 信息弹窗元素
    const createPoiInfoOverlay = () => {
      let overlay = document.getElementById('poiInfoOverlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'poiInfoOverlay';
        overlay.style.position = 'absolute';
        overlay.style.display = 'none';
        overlay.style.padding = '6px 10px';
        overlay.style.backgroundColor = 'white';
        overlay.style.borderRadius = '4px';
        overlay.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
        overlay.style.fontFamily = 'sans-serif';
        overlay.style.fontSize = '12px';
        overlay.style.color = '#000';
        overlay.style.pointerEvents = 'none';
        overlay.style.zIndex = '9999';
        overlay.style.minWidth = '80px';
        overlay.style.textAlign = 'center';
        document.body.appendChild(overlay);
      }
      return overlay;
    };

    // === 清除 建筑物 图层函数 ===
    const clearJianzhuwuLayer = () => {
      if (!viewerRef.current) return;

      // 清除 jianzhuwu_mian
      if (pbfLayersRef.current.jianzhuwu_mian) {
        viewerRef.current.imageryLayers.remove(pbfLayersRef.current.jianzhuwu_mian);
        pbfLayersRef.current.jianzhuwu_mian = undefined;
      }
    };
    // === 加载 建筑物 图层函数 ===
    const loadJianzhuwuLayer = () => {
      if (!viewerRef.current || !viewerReady) {
        return;
      }

      const jianzhuwuStyle: any = {
        version: 8,
        sources: {
          'nanjing-jianzhuwu': {
            type: 'vector',
            tiles: ['https://www.daditu.com.cn/map_resource/nanjing/mvt/{z}/{x}/{y}.mvt'],
          }
        },
        layers: [
          {
            id: 'BUI_S_1@0_10JI-fill',
            type: 'fill',
            source: 'nanjing-jianzhuwu',
            'source-layer': 'BUI_S_1@0_10JI',
            paint: {
              'fill-color': '#a0aec0', // 浅灰色，更明亮
              'fill-outline-color': '#718096',
              'fill-opacity': 0.5
            }
          },
          {
            id: 'BUI_S_1@11_12JI-fill',
            type: 'fill',
            source: 'nanjing-jianzhuwu',
            'source-layer': 'BUI_S_1@11_12JI',
            paint: {
              'fill-color': '#a0aec0', // 浅灰色，更明亮
              'fill-outline-color': '#718096',
              'fill-opacity': 0.5
            }
          },
          {
            id: 'BUI_S_1@13_14JI-fill',
            type: 'fill',
            source: 'nanjing-jianzhuwu',
            'source-layer': 'BUI_S_1@13_14JI',
            paint: {
              'fill-color': '#a0aec0', // 浅灰色，更明亮
              'fill-outline-color': '#718096',
              'fill-opacity': 0.5
            }
          },
          {
            id: 'BUI_S_1@15JI-fill',
            type: 'fill',
            source: 'nanjing-jianzhuwu',
            'source-layer': 'BUI_S_1@15JI',
            paint: {
              'fill-color': '#a0aec0', // 浅灰色，更明亮
              'fill-outline-color': '#718096',
              'fill-opacity': 0.5
            }
          },
          {
            id: 'BUI_S_1@16JI-fill',
            type: 'fill',
            source: 'nanjing-jianzhuwu',
            'source-layer': 'BUI_S_1@16JI',
            paint: {
              'fill-color': '#a0aec0', // 浅灰色，更明亮
              'fill-outline-color': '#718096',
              'fill-opacity': 0.5
            }
          },
          {
            id: 'BUI_S_1@17JI-fill',
            type: 'fill',
            source: 'nanjing-jianzhuwu',
            'source-layer': 'BUI_S_1@17JI',
            paint: {
              'fill-color': '#a0aec0', // 浅灰色，更明亮
              'fill-outline-color': '#718096',
              'fill-opacity': 0.5
            }
          },
          {
            id: 'BUI_S_1@18JI-fill',
            type: 'fill',
            source: 'nanjing-jianzhuwu',
            'source-layer': 'BUI_S_1@18JI',
            paint: {
              'fill-color': '#a0aec0', // 浅灰色，更明亮
              'fill-outline-color': '#718096',
              'fill-opacity': 0.5
            }
          },
        ]
      };

      MVTImageryProvider.fromUrl(jianzhuwuStyle)
        .then((jianzhuwuProvider: any) => {
          const jianzhuwuLayer = viewerRef.current.imageryLayers.addImageryProvider(jianzhuwuProvider);
          jianzhuwuLayer.show = true; // 确保图层显示
          pbfLayersRef.current.jianzhuwu_mian = jianzhuwuLayer;
        })
        .catch((error: any) => {
          console.error('建筑物 MVT图层加载失败:', error);
        });
    };

    // === 清除 建筑物白膜 图层函数 ===
    const clearBuildingWhiteLayer = () => {
      if (!viewerRef.current || !buildingTilesetRef.current) return;

      // 从场景中移除建筑物图层
      viewerRef.current.scene.primitives.remove(buildingTilesetRef.current);
      buildingTilesetRef.current = null;

      viewerRef.current.camera.flyTo({
        destination: viewerRef.current.camera.position,
        orientation: {
          pitch: Cesium.Math.toRadians(-90)
        },
        duration: 2.0
      });
    };
    // === 加载 建筑物白膜 图层函数 ===
    const loadBuildingWhiteLayer = async () => {
      if (!viewerRef.current || !viewerReady) return;

      try {
        const buildingTileset = await Cesium.Cesium3DTileset.fromUrl(
          'https://www.daditu.com.cn/map_resource/nanjing/buis3dTiles/tileset.json'
        );

        viewerRef.current.scene.primitives.add(buildingTileset);
        buildingTilesetRef.current = buildingTileset;

        // 添加自定义着色器实现动态效果
        const customShader = new Cesium.CustomShader({
          lightingModel: Cesium.LightingModel.UNLIT,
          fragmentShaderText: `
          void fragmentMain(FragmentInput fsInput, inout czm_modelMaterial material) {
            // 获取高度属性用于颜色分级
            float height = fsInput.attributes.feature.Height;
            vec3 baseColor;
            
            // 根据高度设置更深的暗蓝色调
            if (height <= 30.0) {
              baseColor = vec3(25.0/255.0, 45.0/255.0, 120.0/255.0); // 更深的蓝
            } else if (height <= 80.0) {
              baseColor = vec3(5.0/255.0, 60.0/255.0, 90.0/255.0);   // 更深的中蓝
            } else {
              baseColor = vec3(25.0/255.0, 45.0/255.0, 120.0/255.0);  // 更深的浅蓝（顶部）
            }
            
            // 应用基础颜色
            material.diffuse = baseColor;
            
            // 添加动态效果但保持整体较暗
            float _baseHeight = 0.0;
            float _heightRange = 10.0;
            float _glowRange = 300.0;
            float vtxf_height = fsInput.attributes.positionMC.z - _baseHeight;
            float vtxf_a11 = fract(czm_frameNumber / 120.0) * 3.14159265 * 2.0;
            float vtxf_a12 = vtxf_height / _heightRange + sin(vtxf_a11) * 0.03; // 进一步减少亮度变化
            
            // 保持整体更深，只轻微调整
            material.diffuse *= (0.7 + vtxf_a12 * 0.15);
            
            // 光环效果也调得更暗
            float vtxf_a13 = fract(czm_frameNumber / 200.0);
            float vtxf_h = clamp(vtxf_height / _glowRange, 0.0, 1.0);
            vtxf_a13 = abs(vtxf_a13 - 0.5) * 0.3; // 进一步降低光环强度
            float vtxf_diff = step(0.01, abs(vtxf_h - vtxf_a13));
            material.diffuse += material.diffuse * (1.0 - vtxf_diff) * 0.15; // 降低高光强度
          }
        `,
        });
        buildingTileset.customShader = customShader;

        // 飞到建筑物区域
        viewerRef.current.camera.flyTo({
          destination: Cesium.Cartesian3.fromDegrees(118.7969, 32.0550, 400),
          orientation: {
            heading: Cesium.Math.toRadians(-10),
            pitch: Cesium.Math.toRadians(-35),
            roll: 0.0
          },
          duration: 2
        });

        // 设置悬停事件处理器
        const cleanupHover = setupBuildingHoverHandler(viewerRef.current, buildingTilesetRef.current);

        // 存储清理函数以便在清除图层时调用
        (viewerRef.current as any).cleanupBuildingHover = cleanupHover;
      } catch (error) {
        console.error('建筑物白膜图层加载失败:', error);
      }
    };
    // 设置 建筑物白膜 悬停事件处理器
    const setupBuildingHoverHandler = (viewer: any, buildingTileset: any) => {
      // 创建名称弹窗元素
      const nameOverlay = document.createElement('div');
      nameOverlay.id = 'buildingNameOverlay';
      nameOverlay.style.position = 'absolute';
      nameOverlay.style.display = 'none';
      nameOverlay.style.padding = '6px 10px';
      nameOverlay.style.backgroundColor = 'white';
      nameOverlay.style.borderRadius = '4px';
      nameOverlay.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
      nameOverlay.style.fontFamily = 'sans-serif';
      nameOverlay.style.fontSize = '12px';
      nameOverlay.style.color = '#000';
      nameOverlay.style.pointerEvents = 'none';
      nameOverlay.style.zIndex = '9999';
      nameOverlay.style.minWidth = '80px';
      nameOverlay.style.textAlign = 'center';
      document.body.appendChild(nameOverlay);

      let lastPickedFeature: any = null;

      // 添加鼠标悬停事件
      viewer.screenSpaceEventHandler.setInputAction((movement: any) => {
        // 恢复上一个选中模型的颜色并隐藏弹窗
        if (lastPickedFeature) {
          lastPickedFeature.color = new Cesium.Color(1, 1, 1, 1);
          lastPickedFeature = null;
          nameOverlay.style.display = 'none';
        }

        const pickedFeature = viewer.scene.pick(movement.endPosition);
        if (Cesium.defined(pickedFeature) && pickedFeature.tileset === buildingTileset) {
          // 高亮当前选中的模型
          pickedFeature.color = Cesium.Color.RED;
          lastPickedFeature = pickedFeature;

          // 获取名称并显示弹窗
          let name = '建筑物';
          if (pickedFeature.hasProperty('name')) {
            name = pickedFeature.getProperty('name');
          }

          nameOverlay.style.display = 'block';
          nameOverlay.style.bottom = (viewer.canvas.clientHeight - movement.endPosition.y + 10) + 'px';
          nameOverlay.style.left = (movement.endPosition.x - nameOverlay.offsetWidth / 2) + 'px';
          nameOverlay.textContent = name;
        }
      }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

      // 返回清理函数
      return () => {
        if (lastPickedFeature) {
          lastPickedFeature.color = new Cesium.Color(1, 1, 1, 1);
        }
        viewer.screenSpaceEventHandler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
        if (document.contains(nameOverlay)) {
          document.body.removeChild(nameOverlay);
        }
      };
    };

    // === 清除 居民地 图层函数 ===
    const clearJumindiLayer = () => {
      if (!viewerRef.current) return;

      // 清除 jumindi_mian
      if (pbfLayersRef.current.jumindi_mian) {
        viewerRef.current.imageryLayers.remove(pbfLayersRef.current.jumindi_mian);
        pbfLayersRef.current.jumindi_mian = undefined;
      }
    };
    // === 加载 居民地 图层函数 ===
    const loadJumindiLayer = () => {
      if (!viewerRef.current || !viewerReady) {
        return;
      }

      const jumindiStyle: any = {
        version: 8,
        sources: {
          'nanjing-jumindi': {
            type: 'vector',
            tiles: ['https://www.daditu.com.cn/map_resource/nanjing/mvt3/{z}/{x}/{y}.mvt'],
          }
        },
        layers: [
          {
            id: 'BUI_SS_1@0_1020260415(0_24)',
            type: 'fill',
            source: 'nanjing-jumindi',
            'source-layer': 'BUI_SS_1@0_1020260415',
            paint: {
              'fill-color': '#4ecdc4',
              'fill-outline-color': '#4ecdc4',
              'fill-opacity': 0.3
            }
          },
          {
            id: 'BUI_SS_1@11_1220260415(0_24)',
            type: 'fill',
            source: 'nanjing-jumindi',
            'source-layer': 'BUI_SS_1@11_1220260415',
            paint: {
              'fill-color': '#4ecdc4',
              'fill-outline-color': '#4ecdc4',
              'fill-opacity': 0.3
            }
          },
          {
            id: 'BUI_SS_1@13_1420260415(0_24)',
            type: 'fill',
            source: 'nanjing-jumindi',
            'source-layer': 'BUI_SS_1@13_1420260415',
            paint: {
              'fill-color': '#4ecdc4',
              'fill-outline-color': '#4ecdc4',
              'fill-opacity': 0.3
            }
          },
          {
            id: 'BUI_SS_1@1520260415(0_24)',
            type: 'fill',
            source: 'nanjing-jumindi',
            'source-layer': 'BUI_SS_1@1520260415',
            paint: {
              'fill-color': '#4ecdc4',
              'fill-outline-color': '#4ecdc4',
              'fill-opacity': 0.3
            }
          },
          {
            id: 'BUI_SS_1@1620260415(0_24)',
            type: 'fill',
            source: 'nanjing-jumindi',
            'source-layer': 'BUI_SS_1@1620260415',
            paint: {
              'fill-color': '#4ecdc4',
              'fill-outline-color': '#4ecdc4',
              'fill-opacity': 0.3
            }
          },
          {
            id: 'BUI_SS_1@17_1820260413(0_24)',
            type: 'fill',
            source: 'nanjing-jumindi',
            'source-layer': 'BUI_SS_1@17_1820260413',
            paint: {
              'fill-color': '#4ecdc4',
              'fill-outline-color': '#4ecdc4',
              'fill-opacity': 0.3
            }
          },
        ]
      };

      MVTImageryProvider.fromUrl(jumindiStyle)
        .then((jumindiProvider: any) => {
          const jumindiLayer = viewerRef.current.imageryLayers.addImageryProvider(jumindiProvider);
          jumindiLayer.show = true; // 确保图层显示
          pbfLayersRef.current.jumindi_mian = jumindiLayer;
        })
        .catch((error: any) => {
          console.error('居民地 MVT图层加载失败:', error);
        });
    };

    // === 清除 场景面 图层函数 ===
    const clearChangjingLayer = () => {
      if (!viewerRef.current) return;

      // 清除 changjing_mian
      if (pbfLayersRef.current.changjing_mian) {
        viewerRef.current.imageryLayers.remove(pbfLayersRef.current.changjing_mian);
        pbfLayersRef.current.changjing_mian = undefined;
      }

      // 清除场景面点击处理器
      if (pbfLayersRef.current.changjingClickHandler) {
        pbfLayersRef.current.changjingClickHandler.destroy();
        pbfLayersRef.current.changjingClickHandler = undefined;
      }

      // 清除临时渲染的3D白膜建筑物
      clearTemporaryBuildings();

      // 回到俯视视角
      const cameraPosition = viewerRef.current.camera.position;
      const cartographic = Cesium.Cartographic.fromCartesian(cameraPosition);
      const longitude = Cesium.Math.toDegrees(cartographic.longitude);
      const latitude = Cesium.Math.toDegrees(cartographic.latitude);
      const height = cartographic.height;

      // 创建以当前位置为中心的边界球
      const center = Cesium.Cartesian3.fromDegrees(longitude, latitude, height / 2);
      const bs = new Cesium.BoundingSphere(center, Math.max(height / 2, 500));

      viewerRef.current.camera.flyToBoundingSphere(bs, {
        duration: 2,
        offset: new Cesium.HeadingPitchRange(
          Cesium.Math.toRadians(0), // heading - 正北方向
          Cesium.Math.toRadians(-90), // pitch - 完全俯视
          Math.max(bs.radius * 2, 1000) // distance
        )
      });
    };
    // === 加载 场景面 图层函数 ===
    const loadChangjingLayer = () => {
      if (!viewerRef.current || !viewerReady) {
        return;
      }

      const changjingStyle: any = {
        version: 8,
        sources: {
          'nanjing-changjing': {
            type: 'vector',
            tiles: ['https://www.daditu.com.cn/map_resource/nanjing/mvt3/{z}/{x}/{y}.mvt'],
          }
        },
        layers: [
          {
            id: '场景_1@0_1020260415(0_24)',
            type: 'fill',
            source: 'nanjing-changjing',
            'source-layer': '场景_1@0_1020260415',
            paint: {
              'fill-color': '#f59e0b',
              'fill-outline-color': '#f59e0b',
              'fill-opacity': 0.3
            }
          },
          {
            id: '场景_1@0_1020260415_outline(0_24)',
            type: 'line',
            source: 'nanjing-changjing',
            'source-layer': '场景_1@0_1020260415',
            paint: {
              'line-color': '#f59e0b',
              'line-width': 1
            }
          },
          {
            id: '场景_1@11_1220260415(0_24)',
            type: 'fill',
            source: 'nanjing-changjing',
            'source-layer': '场景_1@11_1220260415',
            paint: {
              'fill-color': '#f59e0b',
              'fill-outline-color': '#f59e0b',
              'fill-opacity': 0.3
            }
          },
          {
            id: '场景_1@11_1220260415_outline(0_24)',
            type: 'line',
            source: 'nanjing-changjing',
            'source-layer': '场景_1@11_1220260415',
            paint: {
              'line-color': '#f59e0b',
              'line-width': 1
            }
          },
          {
            id: '场景_1@13_1420260415(0_24)',
            type: 'fill',
            source: 'nanjing-changjing',
            'source-layer': '场景_1@13_1420260415',
            paint: {
              'fill-color': '#f59e0b',
              'fill-outline-color': '#f59e0b',
              'fill-opacity': 0.3
            }
          },
          {
            id: '场景_1@13_1420260415_outline(0_24)',
            type: 'line',
            source: 'nanjing-changjing',
            'source-layer': '场景_1@13_1420260415',
            paint: {
              'line-color': '#f59e0b',
              'line-width': 1
            }
          },
          {
            id: '场景_1@1520260415(0_24)',
            type: 'fill',
            source: 'nanjing-changjing',
            'source-layer': '场景_1@1520260415',
            paint: {
              'fill-color': '#f59e0b',
              'fill-outline-color': '#f59e0b',
              'fill-opacity': 0.3
            }
          },
          {
            id: '场景_1@1520260415_outline(0_24)',
            type: 'line',
            source: 'nanjing-changjing',
            'source-layer': '场景_1@1520260415',
            paint: {
              'line-color': '#f59e0b',
              'line-width': 1
            }
          },
          {
            id: '场景_1@1620260415(0_24)',
            type: 'fill',
            source: 'nanjing-changjing',
            'source-layer': '场景_1@1620260415',
            paint: {
              'fill-color': '#f59e0b',
              'fill-outline-color': '#f59e0b',
              'fill-opacity': 0.3
            }
          },
          {
            id: '场景_1@1620260415_outline(0_24)',
            type: 'line',
            source: 'nanjing-changjing',
            'source-layer': '场景_1@1620260415',
            paint: {
              'line-color': '#f59e0b',
              'line-width': 1
            }
          },
          {
            id: '场景_1@17_1820260413(0_24)',
            type: 'fill',
            source: 'nanjing-changjing',
            'source-layer': '场景_1@17_1820260413',
            paint: {
              'fill-color': '#f59e0b',
              'fill-outline-color': '#f59e0b',
              'fill-opacity': 0.3
            }
          },
          {
            id: '场景_1@17_1820260413_outline(0_24)',
            type: 'line',
            source: 'nanjing-changjing',
            'source-layer': '场景_1@17_1820260413',
            paint: {
              'line-color': '#f59e0b',
              'line-width': 1
            }
          },
        ]
      };

      MVTImageryProvider.fromUrl(changjingStyle)
        .then((changjingProvider: any) => {
          const changjingLayer = viewerRef.current.imageryLayers.addImageryProvider(changjingProvider);
          changjingLayer.show = true; // 确保图层显示
          pbfLayersRef.current.changjing_mian = changjingLayer;

          // 添加简单的点击事件监听器 - 输出场景面属性
          const clickHandler = new Cesium.ScreenSpaceEventHandler(viewerRef.current.scene.canvas);
          clickHandler.setInputAction((movement: { position: Cesium.Cartesian2 }) => {

            // 先检查是否点击了临时建筑物或POI图标，如果是则不处理场景面点击
            const pickedObject = viewerRef.current.scene.pick(movement.position);
            if (pickedObject &&
              pickedObject.id &&
              pickedObject.id.properties) {
              if (pickedObject.id.properties.isTemporaryBuilding &&
                pickedObject.id.properties.isTemporaryBuilding.getValue()) {
                return; // 不触发场景面点击事件
              }
              if (pickedObject.id.properties.isPoiPoint &&
                pickedObject.id.properties.isPoiPoint.getValue()) {
                return; // 不触发场景面点击事件
              }
            }

            const pickRay = viewerRef.current.camera.getPickRay(movement.position);
            const featuresPromise = viewerRef.current.imageryLayers.pickImageryLayerFeatures(pickRay, viewerRef.current.scene);

            if (Cesium.defined(featuresPromise)) {
              Promise.resolve(featuresPromise).then((features: any[]) => {
                if (features && features.length > 0) {
                  const firstValue = Object.values(features[0].data)[0];
                  if (firstValue && firstValue[0] && firstValue[0].OBJECTID) {
                    fetchAndRenderBuildingsByObjectId(firstValue[0].OBJECTID);
                  }
                }
              });
            }
          }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

          // 保存处理器引用以便清理
          pbfLayersRef.current.changjingClickHandler = clickHandler;

        })
        .catch((error: any) => {
          console.error('场景面 MVT图层加载失败:', error);
        });
    };
    // 封装：根据场景面 OBJECTID 获取并渲染建筑物与POI
    const fetchAndRenderBuildingsByObjectId = async (objectId: string) => {
      try {
        const { buiList, poiList, LxList } = await fetchBuildingsByObjectId(objectId);

        // 1. 渲染3D白膜建筑物
        if (buiList && Array.isArray(buiList)) {
          renderBuildingWhiteModels(buiList);
        }

        // 2. 设置POI列表数据并显示面板
        if (poiList && Array.isArray(poiList)) {
          setShowSceneList(false);
          setPoiListData(poiList);
          setShowPoiList(true);

          // 3. 在地图上显示POI点
          displayPoiPointsOnMap(poiList);
        } else {
          // 如果没有POI，确保列表关闭
          setShowPoiList(false);
          clearPoiPoints(); // 可选：清除旧POI点
        }

        // 4. 设置LxList数据并显示图例
        if (LxList && Array.isArray(LxList)) {
          setLxListData(LxList);
          setShowPoiLegend(true);
        } else {
          setShowPoiLegend(false);
        }
      } catch (error) {
        console.error('获取场景面建筑物数据失败:', error);
        // 可选：弹出错误提示
      }
    };
    // === 渲染 场景面 3D白膜建筑物 ===
    const renderBuildingWhiteModels = (buildings: any[]) => {
      if (!viewerRef.current || buildings.length === 0) return;

      // 清除之前渲染的临时建筑物
      clearTemporaryBuildings();

      // 计算所有建筑物的边界框和最大高度
      let minLon = Infinity, maxLon = -Infinity;
      let minLat = Infinity, maxLat = -Infinity;
      let maxHeight = 0;

      buildings.forEach(building => {
        try {
          // 解析WKT几何数据
          const polygonCoords = parseWKTPolygon(building.geom);
          if (!polygonCoords || polygonCoords.length === 0) {
            console.warn('无效的建筑物几何数据:', building);
            return;
          }

          // 获取建筑物高度，如果没有height字段，使用floor*3作为估算高度
          const height = building.height || (building.floor ? building.floor * 3 : 10);
          maxHeight = Math.max(maxHeight, height);

          // 更新边界框
          const coords = polygonCoords[0];
          coords.forEach(([lon, lat]) => {
            minLon = Math.min(minLon, lon);
            maxLon = Math.max(maxLon, lon);
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
          });

          // 创建拉伸的3D建筑物
          const positions = coords.map(([lon, lat]) =>
            Cesium.Cartesian3.fromDegrees(lon, lat)
          );

          // 创建墙体（侧面）
          const wallEntity = viewerRef.current.entities.add({
            wall: {
              positions: positions,
              material: Cesium.Color.fromCssColorString("#FFFFFF"), // 纯白色，不透明
              outline: true,
              outlineColor: Cesium.Color.fromCssColorString("#E5E7EB"), // 浅灰色边框,
              outlineWidth: 1,
              minimumHeights: Array(positions.length).fill(0),
              maximumHeights: Array(positions.length).fill(height)
            },
            properties: {
              isTemporaryBuilding: true,
              originalData: building
            }
          });

          // 创建屋顶（顶面）
          const roofEntity = viewerRef.current.entities.add({
            polygon: {
              hierarchy: new Cesium.PolygonHierarchy(positions),
              material: Cesium.Color.fromCssColorString("#FFFFFF"),
              outline: true,
              outlineColor: Cesium.Color.fromCssColorString("#E5E7EB"),
              outlineWidth: 1,
              height: height
            },
            properties: {
              isTemporaryBuilding: true,
              originalData: building
            }
          });

          // 保存临时建筑物引用以便后续清理
          temporaryBuildingsRef.current.push(wallEntity, roofEntity);
        } catch (error) {
          console.error('渲染建筑物出错:', error, building);
        }
      });

      // 调整相机视角以最佳显示3D白膜效果
      if (minLon !== Infinity && maxLon !== -Infinity && minLat !== Infinity && maxLat !== -Infinity) {
        const centerLon = (minLon + maxLon) / 2 - 0.0002; // 稍微偏移中心点，避免完全正中
        const centerLat = (minLat + maxLat) / 2 + 0.0002;

        // 使用边界球方法确保建筑物在视野中央
        const center = Cesium.Cartesian3.fromDegrees(centerLon, centerLat, maxHeight / 2);
        const bs = new Cesium.BoundingSphere(center, Math.max((maxLon - minLon) * 111000, (maxLat - minLat) * 111000, maxHeight));

        viewerRef.current.camera.flyToBoundingSphere(bs, {
          duration: 2,
          offset: new Cesium.HeadingPitchRange(
            Cesium.Math.toRadians(45), // heading
            Cesium.Math.toRadians(-38), // pitch
            Math.max(bs.radius * 2, 400) // distance
          )
        });
      }

      // 设置临时建筑物点击事件处理器
      if (temporaryBuildingsRef.current.length > 0) {
        // 清理之前的点击处理器（如果存在）
        if ((viewerRef.current as any).temporaryBuildingClickHandler) {
          (viewerRef.current as any).temporaryBuildingClickHandler.destroy();
        }
        (viewerRef.current as any).temporaryBuildingClickHandler = setupTemporaryBuildingClickHandler();
      }
    };
    // === 清除 场景面 临时渲染的建筑物 ===
    const clearTemporaryBuildings = () => {
      if (!viewerRef.current) return;

      if (temporaryBuildingsRef.current.length > 0) {
        temporaryBuildingsRef.current.forEach(entity => {
          if (viewerRef.current.entities.contains(entity)) {
            viewerRef.current.entities.remove(entity);
          }
        });
        temporaryBuildingsRef.current = [];
      }

      // 清理点击事件处理器
      if ((viewerRef.current as any).temporaryBuildingClickHandler) {
        (viewerRef.current as any).temporaryBuildingClickHandler.destroy();
        (viewerRef.current as any).temporaryBuildingClickHandler = undefined;
      }

      // 关闭POI列表
      setShowPoiList(false);
      // 隐藏建筑物信息弹窗
      setShowBuildingInfo(false);
      // 清除POI点
      clearPoiPoints();
      // 清除固定点
      clearFixedPoint();
      // 关闭POI详情弹窗
      setShowPoiDetail(false);
      // 关闭POI图例
      setShowPoiLegend(false);
    };
    // === 设置临时建筑物点击事件处理器 ===
    const setupTemporaryBuildingClickHandler = () => {
      if (!viewerRef.current) return;

      const clickHandler = new Cesium.ScreenSpaceEventHandler(viewerRef.current.scene.canvas);

      clickHandler.setInputAction((movement: { position: Cesium.Cartesian2 }) => {
        const pickedObject = viewerRef.current.scene.pick(movement.position);

        // 隐藏之前的弹窗
        setShowBuildingInfo(false);

        if (pickedObject &&
          pickedObject.id &&
          pickedObject.id.properties &&
          pickedObject.id.properties.isTemporaryBuilding &&
          pickedObject.id.properties.isTemporaryBuilding.getValue()) {

          // 获取建筑物原始数据
          const originalData = pickedObject.id.properties.originalData.getValue();

          if (originalData) {
            // 设置建筑物信息数据和位置
            setBuildingInfoData(originalData);
            setBuildingInfoPosition({
              x: movement.position.x,
              y: movement.position.y
            });
            setShowBuildingInfo(true);

          }
        }

        // 点击POI图标 → 同列表点击行为（HTML图标 + 详情弹窗跟随）
        if (pickedObject &&
          pickedObject.id &&
          pickedObject.id.properties &&
          pickedObject.id.properties.isPoiPoint &&
          pickedObject.id.properties.isPoiPoint.getValue()) {

          const poi = pickedObject.id.properties.poiData.getValue();
          if (poi) {
            // 直接从 poiEntitiesRef 找实体索引，确保编号与气泡一致
            const entityIndex = poiEntitiesRef.current.indexOf(pickedObject.id);
            handlePoiClick(poi, entityIndex >= 0 ? entityIndex : undefined);
          }
        }
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

      return clickHandler;
    };
    // 清除所有POI点函数
    const clearPoiPoints = () => {
      if (poiEntitiesRef.current && poiEntitiesRef.current.length > 0) {
        poiEntitiesRef.current.forEach(entity => {
          viewerRef.current.entities.remove(entity);
        });
        poiEntitiesRef.current = []; // 清空数组
      }
    };
    // 清除固定点函数
    const clearFixedPoint = () => {
      setSelectedMarkerIcon(null);
      selectedPoiLonLatRef.current = null;
    };
    // 场景面标签图标：圆角矩形 + 下方箭头（类似POI气泡但更简洁）
    const createSceneLabelIcon = (text: string, categoryColor?: string) => {
      const dpr = window.devicePixelRatio || 1;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      const fontSize = 13;
      const paddingX = 10;
      const rectH = 24;
      const rectR = 5;
      const arrowHeight = 6;
      const arrowHalfWidth = 5;
      const bgColor = categoryColor || '#334155';

      ctx.font = `600 ${fontSize}px "Microsoft YaHei", "PingFang SC", sans-serif`;
      const textWidth = ctx.measureText(text).width;
      const rectW = Math.max(36, textWidth + paddingX * 2);

      const margin = 2;
      const logicalWidth = margin * 2 + rectW;
      const logicalHeight = margin + rectH + arrowHeight + 2;
      canvas.width = logicalWidth * dpr;
      canvas.height = logicalHeight * dpr;
      ctx.scale(dpr, dpr);

      ctx.clearRect(0, 0, logicalWidth, logicalHeight);

      // 阴影
      ctx.shadowColor = 'rgba(0,0,0,0.25)';
      ctx.shadowBlur = 5;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 2;

      // 圆角矩形路径
      const rx = margin;
      const ry = margin;
      const roundRect = (x: number, y: number, w: number, h: number, r: number) => {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        ctx.lineTo(x + w, y + h - r);
        ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        ctx.lineTo(x + r, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        ctx.lineTo(x, y + r);
        ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
      };

      // 填充色（带渐变）
      const gradient = ctx.createLinearGradient(rx, ry, rx, ry + rectH);
      gradient.addColorStop(0, bgColor);
      gradient.addColorStop(1, bgColor);
      ctx.fillStyle = gradient;
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 1;
      roundRect(rx, ry, rectW, rectH, rectR);
      ctx.fill();
      ctx.stroke();

      // 箭头
      const arrowTipX = rx + rectW / 2;
      const arrowTipY = ry + rectH + arrowHeight;
      const arrowBaseY = ry + rectH;
      ctx.save();
      ctx.shadowColor = 'transparent';
      ctx.beginPath();
      ctx.moveTo(arrowTipX, arrowTipY);
      ctx.lineTo(arrowTipX - arrowHalfWidth, arrowBaseY);
      ctx.lineTo(arrowTipX + arrowHalfWidth, arrowBaseY);
      ctx.closePath();
      ctx.fillStyle = bgColor;
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 1;
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      // 白色文字 + 文字描边
      ctx.shadowColor = 'transparent';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.strokeStyle = 'rgba(0,0,0,0.25)';
      ctx.lineWidth = 2;
      ctx.strokeText(text, rx + rectW / 2, ry + rectH / 2);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(text, rx + rectW / 2, ry + rectH / 2);

      return canvas.toDataURL();
    };
    // 创建企业搜索标记图标（参照 POI 气泡，蓝色主题）
    const createEnterpriseMarkerIcon = (name: string) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return '';

      const fontSize = 28;
      const paddingX = 20;
      const rectH = 64;
      const rectR = 16;
      const arrowHeight = 22;
      const arrowHalfWidth = 14;
      const rectX = 15;

      ctx.font = `700 ${fontSize}px "Segoe UI", "Microsoft YaHei", "PingFang SC", Arial, sans-serif`;
      const textWidth = ctx.measureText(name).width;
      const rectW = Math.max(70, textWidth + paddingX * 2);
      const canvasWidth = rectX * 2 + rectW;
      const canvasHeight = rectX + rectH + arrowHeight + 12;
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      // 辅助函数：圆角矩形
      const roundRect = (x: number, y: number, w: number, h: number, r: number) => {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        ctx.lineTo(x + w, y + h - r);
        ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        ctx.lineTo(x + r, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        ctx.lineTo(x, y + r);
        ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
      };

      // 阴影
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 3;

      // 蓝色渐变背景
      const gradient = ctx.createLinearGradient(rectX, rectX, rectX, rectX + rectH);
      gradient.addColorStop(0, '#3b82f6');
      gradient.addColorStop(1, '#2563eb');
      ctx.fillStyle = gradient;
      ctx.strokeStyle = '#1d4ed8';
      ctx.lineWidth = 1.5;
      roundRect(rectX, rectX, rectW, rectH, rectR);
      ctx.fill();
      ctx.stroke();

      // 箭头
      const arrowTipX = rectX + rectW / 2;
      const arrowTipY = rectX + rectH + arrowHeight;
      const arrowBaseY = rectX + rectH;
      ctx.save();
      ctx.shadowColor = 'transparent';
      ctx.beginPath();
      ctx.moveTo(arrowTipX, arrowTipY);
      ctx.lineTo(arrowTipX - arrowHalfWidth, arrowBaseY);
      ctx.lineTo(arrowTipX + arrowHalfWidth, arrowBaseY);
      ctx.closePath();
      ctx.fillStyle = '#2563eb';
      ctx.strokeStyle = '#1d4ed8';
      ctx.lineWidth = 1.5;
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      // 修复矩形与箭头接缝
      ctx.save();
      ctx.shadowColor = 'transparent';
      roundRect(rectX, rectX, rectW, rectH, rectR);
      ctx.fillStyle = gradient;
      ctx.fill();
      ctx.strokeStyle = '#1d4ed8';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();

      // 白色文字
      ctx.shadowColor = 'transparent';
      ctx.font = `700 ${fontSize}px "Segoe UI", "Microsoft YaHei", "PingFang SC", Arial, sans-serif`;
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(name, rectX + rectW / 2, rectX + rectH / 2);

      return canvas.toDataURL();
    };
    // 创建POI图标函数
    const createPoiIcon = (name, poiScene, isSelected = false) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      // 用中文标签（如"居民小区"）映射到正确颜色配置
      const def = getPoiSceneDefByLabel(poiScene);
      const mainColor = def.color;
      const darkColor = def.darkColor;

      // 辅助函数：圆角矩形
      const roundRect = (x, y, w, h, r) => {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        ctx.lineTo(x + w, y + h - r);
        ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        ctx.lineTo(x + r, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        ctx.lineTo(x, y + r);
        ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
      };

      // ===== 固定样式参数 =====
      const fontSize = 28;          // 大字号，清晰可见
      const paddingX = 18;          // 左右内边距
      const rectH = 64;             // 气泡高度
      const rectR = 16;             // 圆角半径
      const arrowHeight = 22;       // 【拉长箭头】从14增加到22，向下延伸更长
      const arrowHalfWidth = 14;    // 箭头底边半宽，同步加宽（原12，现14）

      // 设置字体并测量文字宽度
      ctx.font = `700 ${fontSize}px "Segoe UI", "Microsoft YaHei", "PingFang SC", Arial, sans-serif`;
      const textWidth = ctx.measureText(name).width;
      // 气泡宽度 = 文字宽度 + 左右内边距，最小宽度50（短编号也可）
      const rectW = Math.max(70, textWidth + paddingX * 2);

      // ===== 动态计算画布尺寸 =====
      const rectX = 15;              // 气泡左侧边距（也是画布左留白）
      const canvasWidth = rectX * 2 + rectW;
      const canvasHeight = rectX + rectH + arrowHeight + 12; // 底部留白增加，避免箭头贴边
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      // ===== 绘制阴影 =====
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 3;

      // ===== 绘制气泡背景 =====
      const gradient = ctx.createLinearGradient(rectX, rectX, rectX, rectX + rectH);
      if (isSelected) {
        // 选中状态使用类别深色
        gradient.addColorStop(0, darkColor);
        gradient.addColorStop(1, mainColor);
        ctx.fillStyle = gradient;
        ctx.strokeStyle = darkColor;
      } else {
        // 普通状态使用类别颜色
        gradient.addColorStop(0, mainColor);
        gradient.addColorStop(1, mainColor);
        ctx.fillStyle = gradient;
        ctx.strokeStyle = mainColor;
      }
      ctx.lineWidth = 1.5;
      roundRect(rectX, rectX, rectW, rectH, rectR);
      ctx.fill();
      ctx.stroke();

      // ===== 绘制向下箭头（更长） =====
      const arrowTipX = rectX + rectW / 2;
      const arrowTipY = rectX + rectH + arrowHeight;      // 尖角位置，直接往下延伸
      const arrowBaseY = rectX + rectH;                   // 三角形底边贴合矩形底边

      ctx.save();
      ctx.shadowColor = 'transparent'; // 避免箭头阴影过重
      ctx.beginPath();
      ctx.moveTo(arrowTipX, arrowTipY);
      ctx.lineTo(arrowTipX - arrowHalfWidth, arrowBaseY);
      ctx.lineTo(arrowTipX + arrowHalfWidth, arrowBaseY);
      ctx.closePath();
      if (isSelected) {
        ctx.fillStyle = darkColor;
        ctx.strokeStyle = darkColor;
      } else {
        ctx.fillStyle = mainColor;
        ctx.strokeStyle = mainColor;
      }
      ctx.lineWidth = 1.5;
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      // 修复矩形与箭头接缝（重绘矩形底部）
      ctx.save();
      ctx.shadowColor = 'transparent';
      ctx.beginPath();
      roundRect(rectX, rectX, rectW, rectH, rectR);
      ctx.fillStyle = gradient;
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      // ===== 绘制文字 =====
      ctx.shadowColor = 'transparent';
      ctx.font = `700 ${fontSize}px "Segoe UI", "Microsoft YaHei", "PingFang SC", Arial, sans-serif`;
      ctx.fillStyle = '#ffffff'; // 彩色背景上文字统一白色
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(name, rectX + rectW / 2, rectX + rectH / 2);

      return canvas.toDataURL();
    };
    // 在地图上显示POI点的函数
    const displayPoiPointsOnMap = (poiList: any[]) => {
      // 清除现有的POI点（如果有）
      clearPoiPoints();

      if (poiList && poiList.length > 0) {
        // 在地图上显示所有POI点
        poiList.forEach((poi: any, index: number) => {
          // 创建一个位于POI上方的点，确保它显示在建筑物和其他3D对象之上
          const position = Cesium.Cartesian3.fromDegrees(
            parseFloat(poi.lon),
            parseFloat(poi.lat)
          );

          // 创建POI图标
          const iconUrl = createPoiIcon(index + 1, poi.poiScene || poi.poi_scene, false);
          if (!iconUrl) return;

          const poiEntity = viewerRef.current.entities.add({
            position: position,
            billboard: {
              image: iconUrl,
              scale: 0.5,
              verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
              horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
              // 中间层：限制深度测试距离100km，不会被建筑遮挡，但不会和最高层冲突
              disableDepthTestDistance: 100000,
              pixelOffset: new Cesium.Cartesian2(0, 0)
            },
            properties: {
              isPoiPoint: true,
              poiData: poi
            }
          });

          // 将POI实体添加到数组中，便于统一管理
          poiEntitiesRef.current.push(poiEntity);
        });
      }
    };
    // 点击POI列表 定位到POI位置的函数
    const handlePoiClick = (poi: any, listIndex?: number) => {
      // 清除之前的固定点标记，但保留POI列表点
      clearFixedPoint();

      // 直接使用列表传入的索引，和列表显示的数字保持一致
      const poiIndex = listIndex ?? 0;

      // 创建选中状态的POI图标（与普通POI相同但带有选中效果）
      const iconUrl = createPoiIcon(poiIndex + 1, poi.poiScene || poi.poi_scene, true);
      if (!iconUrl) return;

      // 用 HTML 覆盖层显示选中图标（postRender自动跟踪位置）
      setSelectedMarkerIcon(iconUrl);
      selectedPoiLonLatRef.current = { lon: parseFloat(poi.lon), lat: parseFloat(poi.lat) };

      // 准备数据但不显示弹窗
      setSelectedPoi({ ...poi, clickPos: null });

      // 超时兜底：如果 flyTo complete 没触发，1.5秒后强制显示
      const fallbackTimer = setTimeout(() => {
        setSelectedPoi({ ...poi, clickPos: null });
        setShowPoiDetail(true);
      }, 1500);

      // 飞到 POI 位置（右偏移避免左侧弹窗遮挡），到达后在回调中显示弹窗
      const EAST_OFFSET = 0.0002; // 经度偏移约10米，确保弹窗不被建筑物遮挡
      viewerRef.current.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(
          parseFloat(poi.lon) - EAST_OFFSET,
          parseFloat(poi.lat),
          500
        ),
        duration: 0.8,
        complete: () => {
          clearTimeout(fallbackTimer);
          // 飞行完成 → 算准确屏幕坐标 → 显示弹窗
          let screenPosition = null;
          if (viewerRef.current?.scene) {
            const cp = Cesium.Cartesian3.fromDegrees(
              parseFloat(poi.lon),
              parseFloat(poi.lat)
            );
            const canvasPos = viewerRef.current.scene.cartesianToCanvasCoordinates(cp);
            if (canvasPos && (canvasPos.x > 0 || canvasPos.y > 0)) {
              screenPosition = { x: canvasPos.x, y: canvasPos.y };
            }
          }
          setSelectedPoi({ ...poi, clickPos: screenPosition });
          setShowPoiDetail(true);
        },
      });
    }



    // === 清除 道路中心线 图层函数 ===
    const clearGongluXianLayer = () => {
      if (!viewerRef.current) return;

      // 清除 gonglu_xian
      if (pbfLayersRef.current.gonglu_xian) {
        viewerRef.current.imageryLayers.remove(pbfLayersRef.current.gonglu_xian);
        pbfLayersRef.current.gonglu_xian = undefined;
      }
    };
    // === 加载 道路中心线 图层函数 ===
    const loadGongluXianLayer = () => {
      if (!viewerRef.current || !viewerReady) {
        return;
      }

      const gongluXianStyle: any = {
        version: 8,
        sources: {
          'nanjing-gonglu-xian': {
            type: 'vector',
            tiles: ['https://www.daditu.com.cn/map_resource/nanjing/mvt/{z}/{x}/{y}.mvt'],
          }
        },
        layers: [
          {
            id: 'ROA_S_1@0_10JI-line',
            type: 'line',
            source: 'nanjing-gonglu-xian',
            'source-layer': 'ROA_S_1@0_10JI',
            paint: {
              'line-color': '#10b981',
              'line-width': 2
            }
          },
          {
            id: 'ROA_S_1@11_12JI-line',
            type: 'line',
            source: 'nanjing-gonglu-xian',
            'source-layer': 'ROA_S_1@11_12JI',
            paint: {
              'line-color': '#10b981',
              'line-width': 2
            }
          },
          {
            id: 'ROA_S_1@13_14JI-line',
            type: 'line',
            source: 'nanjing-gonglu-xian',
            'source-layer': 'ROA_S_1@13_14JI',
            paint: {
              'line-color': '#10b981',
              'line-width': 2
            }
          },
          {
            id: 'ROA_S_1@15JI-line',
            type: 'line',
            source: 'nanjing-gonglu-xian',
            'source-layer': 'ROA_S_1@15JI',
            paint: {
              'line-color': '#10b981',
              'line-width': 2
            }
          },
          {
            id: 'ROA_S_1@16JI-line',
            type: 'line',
            source: 'nanjing-gonglu-xian',
            'source-layer': 'ROA_S_1@16JI',
            paint: {
              'line-color': '#10b981',
              'line-width': 2
            }
          },
          {
            id: 'ROA_S_1@17JI-line',
            type: 'line',
            source: 'nanjing-gonglu-xian',
            'source-layer': 'ROA_S_1@17JI',
            paint: {
              'line-color': '#10b981',
              'line-width': 2
            }
          },
          {
            id: 'ROA_S_1@18JI-line',
            type: 'line',
            source: 'nanjing-gonglu-xian',
            'source-layer': 'ROA_S_1@18JI',
            paint: {
              'line-color': '#10b981',
              'line-width': 2
            }
          }]
      };

      MVTImageryProvider.fromUrl(gongluXianStyle)
        .then((gongluXianProvider: any) => {
          const gongluXianLayer = viewerRef.current.imageryLayers.addImageryProvider(gongluXianProvider);
          gongluXianLayer.show = true; // 确保图层显示
          pbfLayersRef.current.gonglu_xian = gongluXianLayer;
        })
        .catch((error: any) => {
          console.error('道路中心线 MVT图层加载失败:', error);
        });
    };

    // === 清除 道路面 图层函数 ===
    const clearGongluMianLayer = () => {
      if (!viewerRef.current) return;

      // 清除 gonglu_mian
      if (pbfLayersRef.current.gonglu_mian) {
        viewerRef.current.imageryLayers.remove(pbfLayersRef.current.gonglu_mian);
        pbfLayersRef.current.gonglu_mian = undefined;
      }
    };
    // === 加载 道路面 图层函数 ===
    const loadGongluMianLayer = () => {
      if (!viewerRef.current || !viewerReady) {
        return;
      }

      const gongluMianStyle: any = {
        version: 8,
        sources: {
          'nanjing-gonglu-mian': {
            type: 'vector',
            tiles: ['https://www.daditu.com.cn/map_resource/nanjing/mvt/{z}/{x}/{y}.mvt'],
          }
        },
        layers: [
          {
            id: 'ROA_S_1@0_10JI-fill',
            type: 'fill',
            source: 'nanjing-gonglu-mian',
            'source-layer': 'ROA_S_1@0_10JI',
            paint: {
              'fill-color': '#45b7d1',
              'fill-outline-color': '#45b7d1',
              'fill-opacity': 0.6
            }
          },
          {
            id: 'ROA_S_1@11_12JI-fill',
            type: 'fill',
            source: 'nanjing-gonglu-mian',
            'source-layer': 'ROA_S_1@11_12JI',
            paint: {
              'fill-color': '#45b7d1',
              'fill-outline-color': '#45b7d1',
              'fill-opacity': 0.6
            }
          },
          {
            id: 'ROA_S_1@13_14JI-fill',
            type: 'fill',
            source: 'nanjing-gonglu-mian',
            'source-layer': 'ROA_S_1@13_14JI',
            paint: {
              'fill-color': '#45b7d1',
              'fill-outline-color': '#45b7d1',
              'fill-opacity': 0.6
            }
          },
          {
            id: 'ROA_S_1@15JI-fill',
            type: 'fill',
            source: 'nanjing-gonglu-mian',
            'source-layer': 'ROA_S_1@15JI',
            paint: {
              'fill-color': '#45b7d1',
              'fill-outline-color': '#45b7d1',
              'fill-opacity': 0.6
            }
          },
          {
            id: 'ROA_S_1@16JI-fill',
            type: 'fill',
            source: 'nanjing-gonglu-mian',
            'source-layer': 'ROA_S_1@16JI',
            paint: {
              'fill-color': '#45b7d1',
              'fill-outline-color': '#45b7d1',
              'fill-opacity': 0.6
            }
          },
          {
            id: 'ROA_S_1@17JI-fill',
            type: 'fill',
            source: 'nanjing-gonglu-mian',
            'source-layer': 'ROA_S_1@17JI',
            paint: {
              'fill-color': '#45b7d1',
              'fill-outline-color': '#45b7d1',
              'fill-opacity': 0.6
            }
          },
          {
            id: 'ROA_S_1@18JI-fill',
            type: 'fill',
            source: 'nanjing-gonglu-mian',
            'source-layer': 'ROA_S_1@18JI',
            paint: {
              'fill-color': '#45b7d1',
              'fill-outline-color': '#45b7d1',
              'fill-opacity': 0.6
            }
          }]
      };

      MVTImageryProvider.fromUrl(gongluMianStyle)
        .then((gongluMianProvider: any) => {
          const gongluMianLayer = viewerRef.current.imageryLayers.addImageryProvider(gongluMianProvider);
          gongluMianLayer.show = true; // 确保图层显示
          pbfLayersRef.current.gonglu_mian = gongluMianLayer;
        })
        .catch((error: any) => {
          console.error('道路面 MVT图层加载失败:', error);
        });
    };

    // === 清除 DEM 图层函数 ===
    const unloadDemTerrain = () => {
      if (!viewerRef.current) return;

      // 1. 恢复默认椭球体地形
      viewerRef.current.terrainProvider = new Cesium.EllipsoidTerrainProvider();
      viewerRef.current.terrainExaggeration = 1.0;
      viewerRef.current.scene.globe.enableLighting = false;
      viewerRef.current.scene.globe.depthTestAgainstTerrain = false;

      // 2. 移除高程材质
      removeElevationMaterial(viewerRef.current);

      // 3. 恢复街道图或卫星图
      updateMapTypeLayers(activeMapType, currentCity);

      // 4. 回到俯视视角
      viewerRef.current.camera.flyTo({
        destination: viewerRef.current.camera.position,
        orientation: {
          pitch: Cesium.Math.toRadians(-90)
        },
        duration: 2.0
      });
    };
    // === 加载 DEM 图层函数 ===
    const loadDemTerrain = () => {
      if (!viewerRef.current) return;

      Cesium.CesiumTerrainProvider.fromUrl(
        'https://www.daditu.com.cn/map_resource/nanjing/dem3dTiles'
      ).then((demTerrainProvider) => {
        console.log('DEM地形加载成功');

        viewerRef.current.terrainProvider = demTerrainProvider;
        viewerRef.current.terrainExaggeration = 1.0;
        viewerRef.current.scene.globe.enableLighting = true;
        viewerRef.current.scene.globe.depthTestAgainstTerrain = true;

        // 使用南京优化参数（紫金山448.9米）
        updateElevationMaterial(viewerRef.current);

        viewerRef.current.scene.mode = Cesium.SceneMode.SCENE3D;
        viewerRef.current.camera.flyTo({
          destination: Cesium.Cartesian3.fromDegrees(118.84, 32.025, 5000),
          orientation: {
            heading: Cesium.Math.toRadians(0),
            pitch: Cesium.Math.toRadians(-45),
            roll: 0.0
          },
          duration: 2.0
        });
      }).catch((error) => {
        console.error('DEM地形加载失败:', error);
      });
    };
    // DEM应用高程色带材质
    const updateElevationMaterial = (
      viewer: Cesium.Viewer,
    ) => {
      // 南京有效范围
      const nanjingBounds = {
        west: Cesium.Math.toRadians(118.3337),
        south: Cesium.Math.toRadians(31.2038),
        east: Cesium.Math.toRadians(119.2584),
        north: Cesium.Math.toRadians(32.6459)
      };

      const layers = [{
        entries: [
          // 10-50米：平原农田 - 深橄榄绿（增加饱和度，降低明度）
          { height: 40, color: new Cesium.Color(0.38, 0.55, 0.20, 0.25) }, // 更浓郁的绿色

          // 50-80米：过渡色 - 深橄榄色（保持实质感）
          { height: 80, color: new Cesium.Color(0.45, 0.58, 0.22, 0.30) },

          // 80-120米：丘陵地带 - 棕褐色（增强饱和度）
          { height: 120, color: new Cesium.Color(0.70, 0.45, 0.22, 0.35) },

          // 120-160米：过渡色 - 土黄色（降低明度，增加实质感）
          { height: 160, color: new Cesium.Color(0.75, 0.60, 0.20, 0.45) },

          // 160-200米：较高丘陵 - 深土黄（避免雾蒙蒙的亮黄）
          { height: 200, color: new Cesium.Color(0.80, 0.65, 0.25, 0.55) },

          // 200-300米：低山 - 橙红褐色（更接近高山质感）
          { height: 300, color: new Cesium.Color(0.82, 0.48, 0.22, 0.75) },

          // 300-450米：中山/高山 - 你的 #ac2f16（保持不变）
          { height: 460, color: new Cesium.Color(0.67, 0.18, 0.09, 0.9) }
        ],
        // 关键：添加边界限制
        extent: new Cesium.Rectangle(
          nanjingBounds.west,
          nanjingBounds.south,
          nanjingBounds.east,
          nanjingBounds.north
        )
      }];

      viewer.scene.globe.material = Cesium.createElevationBandMaterial({
        scene: viewer.scene,
        layers: layers,
      });
    };
    // DEM移除高程材质，恢复默认
    const removeElevationMaterial = (viewer: Cesium.Viewer) => {
      viewer.scene.globe.material = undefined;
    };



    // 监听 楼栋地址 图层显示状态变化
    useEffect(() => {
      if (!viewerReady) return;
      const shouldShowLouyu = activeLayers?.louyu_dian || false;

      if (shouldShowLouyu) {
        // 楼栋地址按钮选中，加载图层
        loadLouyuLayer();
      } else {
        // 楼栋地址按钮取消选中，清除图层
        clearLouyuLayer();
      }
    }, [activeLayers?.louyu_dian, viewerReady]);

    // 监听 POI 图层显示状态变化
    useEffect(() => {
      if (!viewerReady) return;
      const shouldShowPoi = activeLayers?.poi_dian || false;

      if (shouldShowPoi) {
        // POI 按钮选中，加载图层
        loadPbfLayers();
      } else {
        // POI 按钮取消选中，清除图层
        clearPbfLayers();
      }
    }, [activeLayers?.poi_dian, viewerReady]);

    // 监听 建筑物 图层显示状态变化
    useEffect(() => {
      if (!viewerReady) return;
      const shouldShowJianzhuwu = activeLayers?.jianzhuwu_mian || false;

      if (shouldShowJianzhuwu) {
        // 建筑物按钮选中，加载图层
        loadJianzhuwuLayer();
      } else {
        // 建筑物按钮取消选中，清除图层
        clearJianzhuwuLayer();
      }
    }, [activeLayers?.jianzhuwu_mian, viewerReady]);

    // 监听 建筑物白膜 图层的显示状态变化
    useEffect(() => {
      if (!viewerReady) return;
      const shouldShowBuilding = activeLayers?.jianzhuwu_baimo || false;

      if (shouldShowBuilding) {
        // 建筑物白膜按钮选中，加载图层
        loadBuildingWhiteLayer();
      } else {
        // 建筑物白膜按钮取消选中，清除图层
        clearBuildingWhiteLayer();
      }
    }, [activeLayers?.jianzhuwu_baimo, viewerReady]);

    // 监听 居民地 图层显示状态变化
    useEffect(() => {
      if (!viewerReady) return;
      const shouldShowJumindi = activeLayers?.jumindi_mian || false;

      if (shouldShowJumindi) {
        // 居民地按钮选中，加载图层
        loadJumindiLayer();
      } else {
        // 居民地按钮取消选中，清除图层
        clearJumindiLayer();
      }
    }, [activeLayers?.jumindi_mian, viewerReady]);

    // 监听 场景面 图层显示状态变化
    useEffect(() => {
      if (!viewerReady) return;
      const shouldShowChangjing = activeLayers?.changjing_mian || false;

      if (shouldShowChangjing) {
        // 场景面按钮选中，加载图层
        loadChangjingLayer();
      } else {
        // 场景面按钮取消选中，清除图层
        clearChangjingLayer();
      }
    }, [activeLayers?.changjing_mian, viewerReady]);

    // 监听 道路中心线 图层显示状态变化
    useEffect(() => {
      if (!viewerReady) return;
      const shouldShowGongluXian = activeLayers?.gonglu_xian || false;

      if (shouldShowGongluXian) {
        // 道路中心线按钮选中，加载图层
        loadGongluXianLayer();
      } else {
        // 道路中心线按钮取消选中，清除图层
        clearGongluXianLayer();
      }
    }, [activeLayers?.gonglu_xian, viewerReady]);

    // 监听 道路面 图层显示状态变化
    useEffect(() => {
      if (!viewerReady) return;
      const shouldShowGongluMian = activeLayers?.gonglu_mian || false;

      if (shouldShowGongluMian) {
        // 道路面按钮选中，加载图层
        loadGongluMianLayer();
      } else {
        // 道路面按钮取消选中，清除图层
        clearGongluMianLayer();
      }
    }, [activeLayers?.gonglu_mian, viewerReady]);

    // 监听 DEM 图层状态变化
    useEffect(() => {
      if (!viewerReady || !viewerRef.current) {
        return;
      }

      const shouldShowDem = activeLayers?.dem || false;

      // 跳过初始化时的 false 状态（只在用户实际操作后才处理）
      if (!demInitializedRef.current && !shouldShowDem) {
        demInitializedRef.current = true;
        return;
      }

      demInitializedRef.current = true;

      if (shouldShowDem) {
        loadDemTerrain();
        // 切换到卫星图
        setActiveMapType('satellite');
      } else {
        unloadDemTerrain();
        // 切换回街道图
        setActiveMapType('street');
      }
    }, [activeLayers?.dem, viewerReady]);



    // 监听动态数据图层状态变化
    useEffect(() => {
      if (!viewerReady || !viewerRef.current) {
        return;
      }

      // 处理每个动态图层
      Object.keys(DYNAMIC_LAYERS_CONFIG).forEach(key => {
        const layerKey = key as DynamicLayerKey;
        const shouldShowLayer = activeLayers?.[layerKey] || false;

        if (shouldShowLayer) {
          // 如果图层开启，重新加载当前视图的数据
          handleViewChangeForLayer(layerKey);
        } else {
          // 如果图层关闭，清除该图层的所有数据（而不仅仅是隐藏）
          clearDynamicLayer(layerKey);
        }
      });
    }, [activeLayers, viewerReady]);

    // 监听城市变化并定位
    useEffect(() => {
      if (viewerReady && viewerRef.current && city && cityCoordinates) {
        const coords = cityCoordinates[city] || cityCoordinates["南京"];
        const [lat, lng] = coords;

        viewerRef.current.camera.flyTo({
          destination: Cesium.Cartesian3.fromDegrees(lng, lat, 1000),
          orientation: {
            heading: Cesium.Math.toRadians(0),
            pitch: Cesium.Math.toRadians(-90),
            roll: 0.0
          },
          duration: 2
        });

        setCurrentCity(city);

        // 城市变化时更新图层可见性
        // updateMapTypeLayers(activeMapType, city);
      }
    }, [city, cityCoordinates, viewerReady]);

    // 监听地图类型变化
    useEffect(() => {
      if (viewerReady) {
        updateMapTypeLayers(activeMapType, currentCity);
      }
    }, [activeMapType, viewerReady, currentCity]);

    // 监听位置选择并定位到指定地点
    useEffect(() => {
      if (onLocationSelect) {
        (window as any).flyToLocation = (longitude: number, latitude: number, name: string, data?: Record<string, any>) => {
          if (viewerRef.current && viewerReady) {
            // 如果 longitude 和 latitude 都是 0 且 name 为空，表示要清除点位（不移动地图）
            if (longitude === 0 && latitude === 0 && name === '') {
              if (fixedPointEntityRef.current) {
                viewerRef.current.entities.remove(fixedPointEntityRef.current);
                fixedPointEntityRef.current = null;
              }
              enterpriseDataRef.current = null;
              return;
            }

            let calculatedHeight = 1000;

            viewerRef.current.camera.flyTo({
              destination: Cesium.Cartesian3.fromDegrees(longitude, latitude, calculatedHeight),
              orientation: {
                heading: Cesium.Math.toRadians(0),
                pitch: Cesium.Math.toRadians(-90),
                roll: 0.0
              },
              duration: 2
            });

            // 添加企业搜索点位标记（参照 POI 气泡渲染方式）
            if (fixedPointEntityRef.current) {
              viewerRef.current.entities.remove(fixedPointEntityRef.current);
            }

            enterpriseDataRef.current = data || null;

            const enterpriseIconUrl = createEnterpriseMarkerIcon(name);

            fixedPointEntityRef.current = viewerRef.current.entities.add({
              position: Cesium.Cartesian3.fromDegrees(longitude, latitude),
              billboard: {
                image: enterpriseIconUrl,
                scale: 0.6,
                verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
                disableDepthTestDistance: 100000,
                pixelOffset: new Cesium.Cartesian2(0, 0),
              },
              properties: {
                isFixedPoint: true
              }
            });
          }
        };
      }

      return () => {
        if ((window as any).flyToLocation) {
          delete (window as any).flyToLocation;
        }
      };
    }, [onLocationSelect, viewerReady]);




    // 网格搜索 根据 gid 请求面数据并绘制
    const fetchAndDrawRegionByGid = async (gid: string | number, config: any) => {
      if (!viewerRef.current) return;
      try {
        const items = await fetchRegionByGid(gid);
        if (items.length === 0) return;

        // 收集场景元数据用于列表展示
        const sceneMetaList: any[] = [];

        items.forEach((rawData: any, idx: number) => {
          if (!rawData) return;
          const geom = rawData.geom || rawData.GEOM || rawData.wkt || rawData.WKT || rawData.geometry;
          if (!geom) return;
          drawRegionArea({ ...rawData, geom }, config);

          sceneMetaList.push({
            name: rawData.name || rawData.NAME || `场景 ${idx + 1}`,
            extractType: rawData['提取'] || '',
            gid: rawData.gid || rawData.GID || rawData.id || rawData.ID,
            objectid: rawData.objectid || rawData.OBJECTID || '',
            geom: geom,
          });
        });

        setSceneList(sceneMetaList);
        setShowSceneList(sceneMetaList.length > 0);
      } catch (err) {
        console.error('网格搜索画面失败:', err);
      }
    };

    // 监听 网格搜索
    useEffect(() => {
      if (onDataFromGridSearch) {
        let data = onDataFromGridSearch;

        // 根据数据类型进行不同处理
        if (data.type === "district") {
          // 清除之前绘制的区域面
          clearRegionEntity();

          if (!data.data) return; // 清空区县 → 只清除，不绘制

          // 创建模拟配置对象用于渲染
          const config = {
            label: '网格区县',
            color: Cesium.Color.fromCssColorString('#3b82f6'), // 深橙色
            geometryType: 'polygon' as const,
            heightdata: 28000
          };

          // 直接使用返回数据（已有 geom 字段）
          drawRegionArea(data.data, config);
        }
        if (data.type === "street") {
          // 清除之前绘制的区域面
          clearRegionEntity();

          if (!data.data) return; // 清空街道 → 只清除，不绘制

          // 创建模拟配置对象用于渲染
          const config = {
            label: '网格街道',
            color: Cesium.Color.fromCssColorString('#7c3aed'), // 深橙色
            geometryType: 'polygon' as const,
            heightdata: 10000
          };

          // 直接使用返回数据（已有 geom 字段）
          drawRegionArea(data.data, config);
        }
        if (data.type === "scene") {
          // 清除之前绘制的区域面
          clearRegionEntity();

          if (!data.data) return;

          const config = {
            label: '网格搜索场景面',
            color: Cesium.Color.fromCssColorString('#f59e0b'), // 深橙色
            geometryType: 'polygon' as const,
            heightdata: 1000
          };

          const gid = data.data.gid || data.data.id;
          const objectid = data.data.objectid;
          // 有 objectid 时，先画场景面，再从实体属性获取分类决定后续操作
          if (objectid) {
            (async () => {
              if (gid) await fetchAndDrawRegionByGid(gid, config);
              // 从已渲染的实体中查找该场景面，获取完整属性（含分类）
              const entity = [...regionEntityRef.current, ...townAreaEntityRef.current].find(e => {
                try {
                  const props = e.properties?.getValue?.();
                  if (props?.layerType !== '网格搜索场景面') return false;
                  const eGid = props?.gid || props?.originalData?.gid || '';
                  const eObjId = props?.objectid || props?.originalData?.objectid || '';
                  return eGid == gid || eObjId == objectid;
                } catch { return false; }
              });
              const entityProps = entity?.properties?.getValue?.();
              const rawData = entityProps?.originalData || {};
              const sceneType = rawData['提取'] || entityProps?.extractType || '';
              // 打开画像弹窗，并排显示POI列表
              const geom = rawData.geom || rawData.GEOM || '';
              lastClickedSceneGeomRef.current = geom;
              setSceneDetailData({
                name: data.data.name || entityProps?.name || rawData.name || `场景 ${gid}`,
                extractType: sceneType,
                gid: gid || '',
                objectid: objectid || '',
                geom: geom || '',
              });
              setShowSceneDetail(true);
              // 同时加载POI列表（并排显示）
              if (objectid) {
                fetchAndRenderBuildingsByObjectId(objectid);
              }
            })();
          } else if (gid) {
            fetchAndDrawRegionByGid(gid, config);
          }
        }
      }

      return () => {
        if ((window as any).handleGridSearchData) {
          delete (window as any).handleGridSearchData;
        }
      };
    }, [onDataFromGridSearch]);

    // 当 sceneList 变化时重置场景类型筛选
    useEffect(() => {
      if (sceneList.length === 0) {
        setActiveSceneTypes([]);
        return;
      }
      const types = new Set(sceneList.map(item => item.extractType || '未分类'));
      setActiveSceneTypes(Array.from(types));
    }, [sceneList]);
    // 网格搜索 清除行政区面
    const clearRegionEntity = () => {
      if (regionEntityRef.current && viewerRef.current) {
        regionEntityRef.current.forEach(entity => {
          viewerRef.current!.entities.remove(entity);
        });
        regionEntityRef.current = [];
      }
      clearTownAreaEntity();
      clearTemporaryBuildings();
      setSceneList([]);
      setShowSceneList(false);
      setShowSceneDetail(false);
      setSceneDetailData(null);
      highlightScene(null);
      lastClickedSceneGeomRef.current = null;
    };
    // 网格搜索 绘制行政区面
    const drawRegionArea = (data: any, config: any) => {
      if (!viewerRef.current || !data.geom) {
        return;
      }
      // 使用现有的解析WKT多边形函数
      const polygonCoords = parseWKTPolygon(data.geom);
      if (polygonCoords && polygonCoords.length > 0) {
        // 创建模拟数据项
        const item = {
          geom: data.geom,
          ...data
        };
        // 使用现有的渲染多边形函数
        const entities = renderPolygonFeature(item, config, 0);
        if (entities && Array.isArray(entities)) {
          // 保存实体引用以便后续清理
          regionEntityRef.current.push(...entities);
        }

        // 计算经纬度平均值
        const coords = polygonCoords[0];
        let sumLon = 0, sumLat = 0;
        coords.forEach(([lon, lat]) => {
          sumLon += lon;
          sumLat += lat;
        });
        const centerLon = sumLon / coords.length;
        const centerLat = sumLat / coords.length;
        const lonOffset = config.label === '网格搜索场景面' ? 0.002 : 0;
        const centerPosition = Cesium.Cartesian3.fromDegrees(centerLon + lonOffset, centerLat, config.heightdata);

        // 飞到该区域
        viewerRef.current.camera.flyTo({
          destination: centerPosition,
          orientation: {
            heading: Cesium.Math.toRadians(0),
            pitch: Cesium.Math.toRadians(-90),
            roll: 0.0
          },
          duration: 2
        });



      }
    };

    // 网格搜索 设置行政区面、场景面 点击事件处理器
    useEffect(() => {
      if (!viewerRef.current) return;
      const handler = new Cesium.ScreenSpaceEventHandler(viewerRef.current.canvas);

      handler.setInputAction((movement: any) => {
        const pickedObject = viewerRef.current!.scene.pick(movement.position);
        if (Cesium.defined(pickedObject)) {
          const entity = pickedObject.id.properties.getValue();
          if (entity.layerType == '网格街道') {
            handleTownClick(entity.originalData.gid)
          }
          if (entity.layerType == '网格搜索场景面') {
            const rawData = entity.originalData || {};
            const objectid = rawData.objectid || '';
            const sceneType = rawData['提取'] || '';
            const geom = rawData.geom || rawData.GEOM || '';
            // 同时打开POI列表和画像弹窗（并排显示）
            fetchAndRenderBuildingsByObjectId(objectid)
            setShowSceneList(false)
            setSceneDetailData({
              name: rawData.name || `场景 ${objectid}`,
              extractType: sceneType,
              gid: rawData.gid || '',
              objectid: objectid,
              geom: geom,
            });
            setShowSceneDetail(true);
            highlightScene(null)
            // 保存场景面几何，关闭POI列表后可回到该视角
            if (geom) lastClickedSceneGeomRef.current = geom;
            // 飞向场景面中心（经度微偏移避免被左侧弹窗遮挡）
            console.log('flyTo geom:', geom, 'originalData:', rawData);
            const polygonCoords = parseWKTPolygon(geom)?.[0];
            if (polygonCoords && polygonCoords.length > 0) {
              let sumLon = 0, sumLat = 0;
              polygonCoords.forEach(([lon, lat]: number[]) => { sumLon += lon; sumLat += lat; });
              viewerRef.current.camera.flyTo({
                destination: Cesium.Cartesian3.fromDegrees(sumLon / polygonCoords.length + 0.002, sumLat / polygonCoords.length, 1000),
                orientation: { heading: Cesium.Math.toRadians(0), pitch: Cesium.Math.toRadians(-90), roll: 0 },
                duration: 1.5,
              });
            }
          }

          if (entity.isFixedPoint && enterpriseDataRef.current) {
            setEnterpriseDetailData(enterpriseDataRef.current);
            setShowEnterpriseDetail(true);
          }

          console.log('完整属性对象:', entity);
        }
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

      // 清理事件
      return () => handler.destroy();
    }, [viewerRef.current]);

    // 网格搜索 请求场景面接口函数
    const handleTownClick = async (gid: string) => {
      try {
        const data = await fetchScenesByTown(gid);
        // 不清除行政区面（街道外轮廓保留），只清除场景面、建筑物和POI
        clearTownAreaEntity();
        clearTemporaryBuildings();
        clearPoiPoints();
        clearFixedPoint();
        setShowPoiList(false);
        setShowPoiLegend(false);
        setShowPoiDetail(false);
        setSceneList([]);
        setShowSceneList(false);
        highlightScene(null);
        lastClickedSceneGeomRef.current = null;
        // 街道面去掉填充，只保留轮廓线
        regionEntityRef.current.forEach(entity => {
          if (entity.polygon) {
            entity.polygon.material = Cesium.Color.TRANSPARENT;
          }
        });

        renderTownAreaData(data.data)

        // 同步场景面列表
        const rawItems = Array.isArray(data.data) ? data.data : [];
        const sceneMetaList: any[] = [];
        rawItems.forEach((item: any) => {
          const itemGeom = item.geom || item.GEOM || item.wkt || item.WKT || item.geometry || '';
          if (item.name && itemGeom) {
            sceneMetaList.push({
              name: item.name,
              extractType: item['提取'] || '',
              gid: item.gid || item.id || '',
              objectid: item.objectid || item.OBJECTID || '',
              geom: itemGeom,
            });
          }
        });
        if (sceneMetaList.length > 0) {
          setSceneList(sceneMetaList);
          setShowSceneList(true);
        }

        return data;
      } catch (error) {
        console.error("请求 selectCJByTown 接口失败:", error);
        return null;
      }
    };
    // 网格搜索 清除场景面
    const clearTownAreaEntity = () => {
      if (townAreaEntityRef.current && viewerRef.current) {
        townAreaEntityRef.current.forEach(entity => {
          viewerRef.current!.entities.remove(entity);
        });
        townAreaEntityRef.current = [];
      }
    };
    // 场景面 选中高亮
    const highlightScene = (geom: string | null, name: string) => {
      // 清除上一个标签
      if (sceneLabelRef.current && viewerRef.current) {
        viewerRef.current.entities.remove(sceneLabelRef.current);
        sceneLabelRef.current = null;
      }
      // 恢复所有面样式（无颜色加深）
      prevHighlightRef.current.forEach(([entity, fill, outline, width]) => {
        if (entity.polygon) {
          entity.polygon.material = fill;
          entity.polygon.outlineColor = outline;
          entity.polygon.outlineWidth = width;
        }
      });
      prevHighlightRef.current = [];
      if (!geom || !name) return;
      const allEntities = [...townAreaEntityRef.current, ...regionEntityRef.current];
      for (const entity of allEntities) {
        const props = entity.properties?.getValue?.();
        const origData = props?.originalData;
        const entityGeom = origData?.geom || origData?.GEOM || '';
        if (entityGeom === geom && entity.polygon) {
          // 保存当前样式
          prevHighlightRef.current.push([
            entity,
            entity.polygon.material?.clone?.() || entity.polygon.material,
            entity.polygon.outlineColor?.clone?.() || entity.polygon.outlineColor,
            entity.polygon.outlineWidth,
          ]);

          // 颜色加深一点（用 darkColor 提高透明度）
          const extractType = props?.extractType || origData?.['提取'] || '';
          const catDef = extractType ? getPoiSceneDefByLabel(extractType) : null;
          const deepColor = catDef?.darkColor || '#4b5563';
          entity.polygon.material = Cesium.Color.fromCssColorString(deepColor).withAlpha(0.4);
          entity.polygon.outlineColor = Cesium.Color.fromCssColorString(deepColor);
          entity.polygon.outlineWidth = 3;

          // 计算面中心
          const hierarchy = entity.polygon.hierarchy?.getValue?.();
          const positions = hierarchy?.positions;
          if (positions && positions.length > 0) {
            const center = new Cesium.Cartesian3(0, 0, 0);
            positions.forEach((p: any) => {
              center.x += p.x;
              center.y += p.y;
              center.z += p.z;
            });
            center.x /= positions.length;
            center.y /= positions.length;
            center.z /= positions.length;

            // 加标签（气泡图标）
            const extractType = props?.extractType || origData?.['提取'] || '';
            const catDef = extractType ? getPoiSceneDefByLabel(extractType) : null;
            const iconUrl = createSceneLabelIcon(name, catDef?.color);
            if (iconUrl) {
              sceneLabelRef.current = viewerRef.current.entities.add({
                position: center,
                billboard: {
                  image: iconUrl,
                  verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                  horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
                  disableDepthTestDistance: Number.POSITIVE_INFINITY,
                  eyeOffset: new Cesium.Cartesian3(0, 0, 100),
                  pixelOffset: new Cesium.Cartesian2(0, 0),
                },
                properties: { isSceneLabel: true },
              });
            }
          }
          break;
        }
      }
    };
    // === 自定义网格绘制函数 ===
    const pointsToWKT = (pts: [number, number][]): string => {
      if (pts.length < 3) return '';
      const coordsStr = pts.map(([lon, lat]) => `${lon} ${lat}`).join(',');
      const [firstLon, firstLat] = pts[0];
      const [lastLon, lastLat] = pts[pts.length - 1];
      const isClosed = Math.abs(firstLon - lastLon) < 1e-10 &&
        Math.abs(firstLat - lastLat) < 1e-10;
      if (isClosed) {
        return `MULTIPOLYGON(((${coordsStr})))`;
      }
      return `MULTIPOLYGON(((${coordsStr},${firstLon} ${firstLat})))`;
    };
    const clearCustomGridVisuals = () => {
      if (!viewerRef.current) return;
      customGridVisualEntitiesRef.current.forEach(e => viewerRef.current!.entities.remove(e));
      customGridVisualEntitiesRef.current = [];
    };
    const updateCustomGridVisuals = () => {
      if (!viewerRef.current) return;
      clearCustomGridVisuals();
      const pts = customGridPointsRef.current;
      if (pts.length === 0) return;
      const viewer = viewerRef.current;
      const entities: Cesium.Entity[] = [];
      // Vertex dots
      pts.forEach(([lon, lat]) => {
        entities.push(viewer.entities.add({
          position: Cesium.Cartesian3.fromDegrees(lon, lat),
          point: {
            pixelSize: 8,
            color: Cesium.Color.fromCssColorString('#374151'),
            outlineColor: Cesium.Color.WHITE,
            outlineWidth: 2,
          },
        }));
      });
      // Connecting line — 闭合到第一个点
      if (pts.length >= 2) {
        const polyPositions = pts.map(([lon, lat]) => Cesium.Cartesian3.fromDegrees(lon, lat));
        if (pts.length >= 3) {
          polyPositions.push(polyPositions[0]); // 闭合回第一个点
        }
        entities.push(viewer.entities.add({
          polyline: {
            positions: polyPositions,
            material: Cesium.Color.fromCssColorString('#374151').withAlpha(0.8),
            width: 2,
            clampToGround: true,
          },
        }));
      }
      // Preview polygon
      if (pts.length >= 3) {
        entities.push(viewer.entities.add({
          polygon: {
            hierarchy: new Cesium.PolygonHierarchy(pts.map(([lon, lat]) => Cesium.Cartesian3.fromDegrees(lon, lat))),
            material: Cesium.Color.fromCssColorString('#9ca3af').withAlpha(0.15),
            outline: true,
            outlineColor: Cesium.Color.fromCssColorString('#374151'),
            outlineWidth: 2,
          },
        }));
      }
      customGridVisualEntitiesRef.current = entities;
    };
    const commitCustomGridPolygon = () => {
      if (!viewerRef.current) return;
      const pts = customGridPointsRef.current;
      if (pts.length < 3) return;
      const id = ++customGridIdCounterRef.current;
      const entity = viewerRef.current.entities.add({
        polygon: {
          hierarchy: new Cesium.PolygonHierarchy(pts.map(([lon, lat]) => Cesium.Cartesian3.fromDegrees(lon, lat))),
          material: Cesium.Color.fromCssColorString('#6b7280').withAlpha(0.12),
        },
        properties: {
          originalData: {
            id,
            points: pts,
            geom: pointsToWKT(pts),
            name: `自定义网格 #${id}`,
            type: 'custom_grid',
          },
          layerType: 'custom_grid',
        },
      });
      // 额外画一条 polyline 作为边框（Cesium polygon outlineWidth 通常不生效）
      const outlinePositions = pts.map(([lon, lat]) => Cesium.Cartesian3.fromDegrees(lon, lat));
      outlinePositions.push(outlinePositions[0]); // 闭合
      const outlineEntity = viewerRef.current.entities.add({
        polyline: {
          positions: outlinePositions,
          material: Cesium.Color.fromCssColorString('#374151'),
          width: 2,
          clampToGround: true,
        },
      });
      customGridPolygonsRef.current.push({ id, points: pts, entity, outlineEntity });
      customGridPointsRef.current = [];
      clearCustomGridVisuals();
      committedRef.current = true;
      if (viewerRef.current?.scene?.canvas) {
        viewerRef.current.scene.canvas.style.cursor = '';
      }
      setDrawPointCount(0);
      setDrawPolygonCount(customGridPolygonsRef.current.length);
    };
    const clearAllCustomGridPolygons = () => {
      if (!viewerRef.current) return;
      customGridPolygonsRef.current.forEach(({ entity, outlineEntity }) => {
        viewerRef.current!.entities.remove(entity);
        if (outlineEntity) viewerRef.current!.entities.remove(outlineEntity);
      });
      customGridPolygonsRef.current = [];
      customGridPointsRef.current = [];
      clearCustomGridVisuals();
      committedRef.current = false;
      setDrawPointCount(0);
      setDrawPolygonCount(0);
      clearTemporaryBuildings();
    };
    const undoCustomGridPoint = () => {
      if (!viewerRef.current) return;
      if (committedRef.current) {
        const list = customGridPolygonsRef.current;
        if (list.length === 0) { committedRef.current = false; return; }
        const last = list.pop()!;
        viewerRef.current.entities.remove(last.entity);
        if (last.outlineEntity) viewerRef.current.entities.remove(last.outlineEntity);
        setDrawPolygonCount(list.length);
        if (list.length === 0) {
          committedRef.current = false;
          if (viewerRef.current?.scene?.canvas) {
            viewerRef.current.scene.canvas.style.cursor = 'crosshair';
          }
        }
        return;
      }
      const pts = customGridPointsRef.current;
      if (pts.length === 0) return;
      pts.pop();
      updateCustomGridVisuals();
      setDrawPointCount(pts.length);
    };
    // 网格搜索 渲染场景面
    const renderTownAreaData = (data: any[]) => {
      if (!viewerRef.current || !Array.isArray(data) || data.length === 0) {
        return;
      }

      // 清除之前的面实体
      clearTownAreaEntity();

      let allCoords: number[][] = []; // 收集所有顶点 [lon, lat]

      data.forEach((item, index) => {
        if (item.geom && item.name) {
          const config = {
            endpoint: 'town_area',
            color: Cesium.Color.fromCssColorString('#f59e0b'),
            label: "网格搜索场景面",
            geometryType: 'polygon' as const
          };

          const entities = renderPolygonFeature(item, config, index);
          if (entities && Array.isArray(entities)) {
            townAreaEntityRef.current.push(...entities);
          }

          // 解析几何以获取坐标用于定位
          const polygonCoords = parseWKTPolygon(item.geom);
          if (polygonCoords) {
            polygonCoords.forEach(ring => {
              allCoords.push(...ring);
            });
          }
        }
      });

      // 如果有坐标，飞到该区域
      if (data.length > 0 && data[0].geom) {
        const coords = parseWKTPolygon(data[0].geom)?.[0];
        if (coords && coords.length > 0) {
          const sum = coords.reduce((acc, [lon, lat]) => {
            acc.lon += lon;
            acc.lat += lat;
            return acc;
          }, { lon: 0, lat: 0 });
          const centerLon = sum.lon / coords.length;
          const centerLat = sum.lat / coords.length;
          viewerRef.current.camera.flyTo({
            destination: Cesium.Cartesian3.fromDegrees(centerLon, centerLat, 1000),
            orientation: {
              heading: Cesium.Math.toRadians(0),
              pitch: Cesium.Math.toRadians(-90),
              roll: 0
            },
            duration: 2
          });
        }
      }
    };

    // === 自定义网格绘制 — 事件处理器 ===
    useEffect(() => {
      if (!viewerRef.current) return;
      const viewer = viewerRef.current;
      const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

      if (drawingMode) {
        viewer.scene.canvas.style.cursor = 'crosshair';
        handler.setInputAction((e: any) => {
          if (!viewerRef.current?.scene?.globe || committedRef.current) return;
          const ray = viewerRef.current.camera.getPickRay(e.position);
          if (!ray) return;
          const cartesian = viewerRef.current.scene.globe.pick(ray, viewerRef.current.scene);
          if (!cartesian) return;
          const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
          const lon = Cesium.Math.toDegrees(cartographic.longitude);
          const lat = Cesium.Math.toDegrees(cartographic.latitude);
          customGridPointsRef.current.push([lon, lat]);
          updateCustomGridVisuals();
          setDrawPointCount(customGridPointsRef.current.length);
        }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

        handler.setInputAction(() => {
          commitCustomGridPolygon();
        }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

        // 暴露给工具栏按钮
        (window as any).__commitCustomGrid = commitCustomGridPolygon;
        (window as any).__undoCustomGrid = undoCustomGridPoint;
        (window as any).__clearCustomGrid = clearAllCustomGridPolygons;
      }

      return () => {
        handler.destroy();
        // 只清理临时视觉元素，不清理已完成的多边形
        clearCustomGridVisuals();
        customGridPointsRef.current = [];
        committedRef.current = false;
        setDrawPointCount(0);
        delete (window as any).__commitCustomGrid;
        delete (window as any).__undoCustomGrid;
        delete (window as any).__clearCustomGrid;
      };
    }, [drawingMode]);

    // === 退出绘制模式时清除所有自定义网格 ===
    useEffect(() => {
      if (!drawingMode && customGridPolygonsRef.current.length > 0) {
        clearAllCustomGridPolygons();
      }
    }, [drawingMode]);

    // === 自定义网格点击处理器（持久化，独立于临时建筑物生命周期） ===
    useEffect(() => {
      if (!viewerRef.current) return;
      const viewer = viewerRef.current;
      const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
      handler.setInputAction((movement: { position: Cesium.Cartesian2 }) => {
        const pickedObject = viewerRef.current?.scene?.pick(movement.position);
        if (!pickedObject || !pickedObject.id || !pickedObject.id.properties) return;
        const props = pickedObject.id.properties.getValue?.();
        if (props?.layerType === 'custom_grid' && props?.originalData?.geom) {
          const wkt = props.originalData.geom;
          fetchByWKT(wkt).then(({ buiList, poiList, LxList }: any) => {
              console.log('[自定义网格] selectByWKT返回:', { buiList, poiList, LxList });
              // 清除之前的临时数据
              clearTemporaryBuildings();
              // 1. 渲染3D白膜建筑物
              if (buiList && Array.isArray(buiList)) {
                renderBuildingWhiteModels(buiList);
              }
              // 2. 设置POI列表并显示
              if (poiList && Array.isArray(poiList)) {
                setPoiListData(poiList);
                setShowPoiList(true);
                displayPoiPointsOnMap(poiList);
              } else {
                setShowPoiList(false);
                clearPoiPoints();
              }
              // 3. 设置图例
              if (LxList && Array.isArray(LxList)) {
                setLxListData(LxList);
                setShowPoiLegend(true);
              } else {
                setShowPoiLegend(false);
              }
            })
            .catch(err => console.error('[自定义网格] selectByWKT失败:', err));
        }
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
      return () => handler.destroy();
    }, [viewerReady]);

    // === POI列表和画像弹窗都关闭时 → 回到场景面列表 ===
    useEffect(() => {
      if (!showPoiList && !showSceneDetail && lastClickedSceneGeomRef.current) {
        setShowSceneList(true);
        const targetGeom = lastClickedSceneGeomRef.current;
        if (targetGeom && viewerRef.current) {
          const coords = parseWKTPolygon(targetGeom)?.[0];
          if (coords && coords.length > 0) {
            let sumLon = 0, sumLat = 0;
            coords.forEach(([lon, lat]: number[]) => { sumLon += lon; sumLat += lat; });
            viewerRef.current.camera.flyTo({
              destination: Cesium.Cartesian3.fromDegrees(sumLon / coords.length, sumLat / coords.length, 1000),
              orientation: {
                heading: Cesium.Math.toRadians(0),
                pitch: Cesium.Math.toRadians(-90),
                roll: 0,
              },
              duration: 2,
            });
          }
        }
      }
    }, [showPoiList, showSceneDetail]);

    return (
      <div className="w-full h-screen flex flex-col bg-black">
        {headerComponent && <header>{headerComponent}</header>}
        <div
          ref={cesiumContainerRef}
          id="cesiumContainer"
          className="absolute top-0 left-0 w-full h-full"
        />
        {/* 地图类型切换器 */}
        {/* 场景面图例 */}
        {showSceneList && sceneList.length > 0 && (
          <div className="absolute bottom-24 right-4 z-[1000]">
            <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-slate-200 overflow-hidden">
              <div className="px-3 py-2 space-y-1.5">
                <div className="flex items-center gap-1 mb-1">
                  <button
                    onClick={toggleAllSceneTypes}
                    className="flex items-center justify-center w-4 h-4 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors cursor-pointer"
                    title={activeSceneTypes.length > 0 ? '全部取消' : '全部选中'}
                  >
                    <i className={`${activeSceneTypes.length > 0 ? 'ri-checkbox-multiple-line' : 'ri-checkbox-multiple-blank-line'} text-[11px]`} />
                  </button>
                  <span className="text-[11px] font-semibold text-slate-500">场景类型</span>
                </div>
                {Array.from(
                  (() => {
                    const m = new Map<string, number>();
                    sceneList.forEach(item => {
                      const t = item.extractType || '未分类';
                      m.set(t, (m.get(t) || 0) + 1);
                    });
                    return m;
                  })().entries()
                ).map(([type, count]) => {
                  const def = type !== '未分类'
                    ? (() => { try { return getPoiSceneDefByLabel(type); } catch { return null; } })()
                    : null;
                  const dotColor = def?.color || '#6b7280';
                  return (
                    <div
                      key={type}
                      onClick={() => toggleSceneType(type)}
                      className="flex items-center justify-between gap-3 px-2 py-1 rounded hover:bg-slate-100 cursor-pointer transition-all"
                      style={{ opacity: activeSceneTypes.includes(type) ? 1 : 0.35 }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: dotColor }} />
                        <span className="text-xs text-slate-600 whitespace-nowrap">{type}</span>
                      </div>
                      <span className="text-[11px] font-semibold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        {/* 地图类型切换器 */}
        <div
          className="absolute bottom-8 right-4 z-[1000] flex rounded-lg overflow-hidden border border-slate-200"
          style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.12)' }}
        >
          {(['street', 'satellite'] as MapType[]).map((t) => (
            <button
              key={t}
              onClick={() => setActiveMapType(t)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs cursor-pointer transition-all whitespace-nowrap ${t === 'satellite' ? 'border-l border-slate-200' : ''
                } ${activeMapType === t ? 'bg-slate-800 text-white font-semibold' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              <i className={`${t === 'street' ? 'ri-road-map-line' : 'ri-satellite-line'} text-sm`}></i>
              {t === 'street' ? '街道图' : '卫星图'}
            </button>
          ))}
        </div>

        {/* 建筑物信息弹窗 */}
        <BuildingInfo
          buildingData={buildingInfoData}
          position={buildingInfoPosition}
          visible={showBuildingInfo}
          onClose={() => setShowBuildingInfo(false)}
        />

        {/* 建筑物/POI信息列表 */}
        <BuildingInfoList
          pois={poiListData}
          visible={showPoiList}
          onPoiClick={handlePoiClick}
          layerControlCollapsed={layerControlCollapsed}
          scenePanelCollapsed={scenePanelCollapsed}
          sceneDetailVisible={showSceneDetail}
          onClose={() => {
            clearTemporaryBuildings();
            clearPoiPoints();
            clearFixedPoint();
            setShowPoiList(false);
            setShowPoiLegend(false);
            setShowPoiDetail(false);
          }}
        />

        {/* POI详情弹窗 */}
        <PointPage
          poidata={selectedPoi}
          visible={showPoiDetail}
          onClose={() => { clearFixedPoint(); setShowPoiDetail(false); }}
          followRef={popupPosRef}
        />

        {/* POI图例 */}
        <PoiLegend
          data={lxListData}
          visible={showPoiLegend}
        />

        {/* 网格搜索 场景面列表 */}
        {showSceneList && sceneList.length > 0 && (
          <div
            className="absolute top-36 z-[30]"
            style={{ width: 320, left: layerControlCollapsed ? 62 : 190 }}
          >
            <div className="bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
                <div className="flex items-center gap-2">
                  <i className="ri-grid-line text-blue-500 text-sm" />
                  <h3 className="text-sm font-semibold text-slate-700">场景面列表</h3>
                  <span className="text-[11px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full font-medium">
                    {activeSceneTypes.length < new Set(sceneList.map(i => i.extractType || '未分类')).size
                      ? `${sceneList.filter(i => activeSceneTypes.includes(i.extractType || '未分类')).length}/${sceneList.length}`
                      : sceneList.length}
                  </span>
                </div>
                <button
                  onClick={() => clearRegionEntity()}
                  className="flex items-center justify-center w-6 h-6 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer"
                  title="关闭"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              </div>
              <div className="max-h-[480px] overflow-y-auto">
                {sceneList
                  .filter(item => activeSceneTypes.includes(item.extractType || '未分类'))
                  .map((item, index) => {
                  const def = item.extractType
                    ? (() => { try { return getPoiSceneDefByLabel(item.extractType); } catch { return null; } })()
                    : null;
                  const dotColor = def?.color || '#6b7280';
                  return (
                    <div
                      key={index}
                      onClick={() => {
                        if (item.geom && viewerRef.current) {
                          highlightScene(item.geom, item.name);
                          lastClickedSceneGeomRef.current = item.geom;
                          const coords = parseWKTPolygon(item.geom)?.[0];
                          if (coords && coords.length > 0) {
                            const sum = coords.reduce((acc: any, [lon, lat]: number[]) => {
                              acc.lon += lon;
                              acc.lat += lat;
                              return acc;
                            }, { lon: 0, lat: 0 });
                            const centerLon = sum.lon / coords.length;
                            const centerLat = sum.lat / coords.length;
                            viewerRef.current.camera.flyTo({
                              destination: Cesium.Cartesian3.fromDegrees(centerLon, centerLat, 1000),
                              orientation: {
                                heading: Cesium.Math.toRadians(0),
                                pitch: Cesium.Math.toRadians(-90),
                                roll: 0,
                              },
                              duration: 1.5,
                            });
                          }
                        }
                      }}
                      className="flex items-center gap-2.5 px-4 py-2.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors cursor-pointer"
                    >
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: dotColor }}
                      />
                      <span className="text-xs text-slate-700 truncate flex-1">{item.name}</span>
                      {item.extractType && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded-full text-white font-medium flex-shrink-0"
                          style={{ backgroundColor: dotColor }}
                        >
                          {item.extractType}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* 网格搜索 场景面详情弹窗 */}
        <SceneDetailPanel
          scene={sceneDetailData}
          visible={showSceneDetail}
          onClose={() => { setShowSceneDetail(false); setSceneDetailData(null); }}
          layerControlCollapsed={layerControlCollapsed}
          collapsed={scenePanelCollapsed}
          onCollapsedChange={setScenePanelCollapsed}
        />

        {/* 选中POI图标（HTML覆盖层，永远在最顶层，postRender每帧更新位置） */}
        {selectedMarkerIcon && (
          <img
            ref={selectedMarkerRef}
            src={selectedMarkerIcon}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              transform: 'translate(-50%, -100%) scale(0.55)',
              transformOrigin: 'bottom center',
              zIndex: 999999,
              pointerEvents: 'none',
            }}
            alt=""
          />
        )}

        {/* 自定义网格绘制工具栏 */}
        {drawingMode && (
          <div
            className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[1000]
                       flex items-center gap-2 bg-slate-800 text-white
                       rounded-xl px-4 py-2.5"
            style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.25)' }}
          >
            <i className="ri-pencil-line text-slate-300 text-sm" />
            <span className="text-xs text-slate-200 mr-1">
              {drawPointCount === 0
                ? '点击地图开始绘制网格范围'
                : `已添加 ${drawPointCount} 个顶点`}
            </span>

            <div className="w-px h-4 bg-slate-600 mx-1" />

            <button onClick={() => (window as any).__undoCustomGrid?.()}
              disabled={drawPointCount === 0 && drawPolygonCount === 0}
              className="flex items-center gap-1 px-2 py-1 bg-slate-700 hover:bg-slate-600
                         disabled:opacity-40 disabled:cursor-not-allowed rounded
                         text-xs cursor-pointer transition-colors whitespace-nowrap">
              <i className="ri-arrow-go-back-line text-xs" />撤销
            </button>

            <button onClick={() => commitCustomGridPolygon()}
              disabled={drawPointCount < 3 || committedRef.current}
              className="flex items-center gap-1 px-2 py-1 bg-white text-slate-800
                         hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed
                         rounded text-xs cursor-pointer transition-colors font-medium">
              <i className="ri-check-line text-xs" />完成绘制
            </button>

            {drawPointCount > 0 && (
              <button onClick={() => { customGridPointsRef.current = []; clearCustomGridVisuals(); setDrawPointCount(0); }}
                className="flex items-center gap-1 px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded text-xs cursor-pointer transition-colors">
                <i className="ri-delete-back-2-line text-xs" />清除当前
              </button>
            )}

            {drawPolygonCount > 0 && (
              <button onClick={() => clearAllCustomGridPolygons()}
                className="flex items-center gap-1 px-2 py-1 bg-red-600/80 hover:bg-red-600 rounded text-xs cursor-pointer transition-colors">
                <i className="ri-delete-bin-line text-xs" />
                清除全部 ({drawPolygonCount})
              </button>
            )}
          </div>
        )}

        {/* 企业搜索点位详情弹窗 */}
        <EnterpriseDetailModal
          visible={showEnterpriseDetail}
          data={enterpriseDetailData}
          onClose={() => { setShowEnterpriseDetail(false); setEnterpriseDetailData(null); }}
        />

      </div>
    );
  };

export default MapPage;